import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import api from '../config/axios';
import { useTranslation } from '../hooks/useTranslation';
import {
  FiSave, FiRefreshCw, FiSmartphone, FiType, FiEye,
  FiToggleLeft, FiGlobe, FiPackage, FiImage, FiPlus, FiCheck, FiTrash2, FiStar, FiLayers, FiAward, FiCrop, FiX,
  FiSun, FiMoon, FiMapPin, FiDroplet, FiEdit3, FiClipboard, FiZap, FiCircle, FiLayout, FiLock, FiHome, FiMap, FiUpload,
  FiCalendar, FiGift, FiShare2, FiLink, FiMessageCircle, FiGrid, FiMove
} from 'react-icons/fi';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import StringsTab from '../components/StringsTab';
import LanguageTabsEditor from '../components/LanguageTabsEditor';
import GalleryImagePicker from '../components/GalleryImagePicker';
import IconPicker, { LucideIcon } from '../components/IconPicker';

const API_URL = process.env.REACT_APP_API_URL || 'https://cms.exploreandwin.com/api';

const AppSettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [config, setConfig] = useState(null);
  const [colors, setColors] = useState([]);
  const [typography, setTypography] = useState([]);
  const [features, setFeatures] = useState([]);
  const [strings, setStrings] = useState([]);
  const [colorThemeTab, setColorThemeTab] = useState('light');
  const [lightColors, setLightColors] = useState({
    palette: {}, status: {}, background: {}, text: {}, button: {}, input: {}, divider: ''
  });
  const [darkColors, setDarkColors] = useState({
    palette: {}, status: {}, background: {}, text: {}, button: {}, input: {}, divider: ''
  });
  const [screenBackgrounds, setScreenBackgrounds] = useState({
    light: {}, dark: {}
  });
  const [splashLogoPreview, setSplashLogoPreview] = useState(null);
  const [splashBgPreview, setSplashBgPreview] = useState(null);
  const [appBgImagePreview, setAppBgImagePreview] = useState(null);
  const [loginBgPreview, setLoginBgPreview] = useState(null);
  const [poweredByLogoPreview, setPoweredByLogoPreview] = useState(null);
  const [mapSectionBgPreview, setMapSectionBgPreview] = useState(null);
  const [achievementsSectionBgPreview, setAchievementsSectionBgPreview] = useState(null);
  const [settingsSectionBgPreview, setSettingsSectionBgPreview] = useState(null);

  // Onboarding screens state
  const [onboarding, setOnboarding] = useState({
    enabled: true,
    skipEnabled: true,
    screens: [
      { backgroundImage: { light: '', dark: '' }, headline: '', text: '' },
      { backgroundImage: { light: '', dark: '' }, headline: '', text: '' },
      { backgroundImage: { light: '', dark: '' }, headline: '', text: '' }
    ]
  });

  // Crop modal state for onboarding images
  const [cropModal, setCropModal] = useState({
    isOpen: false,
    imageUrl: '',
    screenIndex: null,
    mode: null // 'light' or 'dark'
  });
  const [crop, setCrop] = useState({ unit: '%', width: 80, height: 80, x: 10, y: 10 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [croppingSaving, setCroppingSaving] = useState(false);
  const cropImgRef = useRef(null);

  // Image preview modal state
  const [imagePreview, setImagePreview] = useState({ isOpen: false, url: '', title: '' });

  // Teasers state
  const [teasers, setTeasers] = useState([]);
  const [expandedTeaserTranslations, setExpandedTeaserTranslations] = useState({});

  // Challenges and events for teaser dropdowns
  const [allChallenges, setAllChallenges] = useState([]);
  const [allEvents, setAllEvents] = useState([]);

  // Background image picker for teasers
  const [showBgImagePicker, setShowBgImagePicker] = useState({ open: false, teaserIndex: null });
  const bgImageInputRefs = useRef({});

  // Main image picker for teasers (imageUrl field)
  const [showMainImagePicker, setShowMainImagePicker] = useState({ open: false, teaserIndex: null });

  // Share settings state
  const [shareSettings, setShareSettings] = useState({
    enabled: true,
    linkFormat: 'https://exploreandwin.com/c/{id}',
    textTemplate: 'Check out this challenge: {name} - {link}',
    platforms: {
      whatsapp: { enabled: true, label: 'WhatsApp', icon: 'whatsapp' },
      x: { enabled: true, label: 'X', icon: 'x-twitter' },
      facebook: { enabled: true, label: 'Facebook', icon: 'facebook' },
      telegram: { enabled: false, label: 'Telegram', icon: 'telegram' },
      instagram: { enabled: false, label: 'Instagram', icon: 'instagram', note: 'Stories only' },
      copyLink: { enabled: true, label: 'Copy Link', icon: 'link' },
      nativeShare: { enabled: true, label: 'More...', icon: 'share' }
    }
  });
  const [savingShareSettings, setSavingShareSettings] = useState(false);

  // Multi-language support state
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translations, setTranslations] = useState({});
  // Language management state
  const [showAddLanguageModal, setShowAddLanguageModal] = useState(false);
  const [newLanguageForm, setNewLanguageForm] = useState({ code: '', name: '', nativeName: '' });
  const [savingLanguage, setSavingLanguage] = useState(false);

  // Categories state
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', icon: 'map-pin', color: '#6B7280', markerIconUrl: '' });
  const [uploadingMarkerIcon, setUploadingMarkerIcon] = useState(null); // category ID being uploaded
  const [availableIcons, setAvailableIcons] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({}); // Challenge count per category

  // Track if initial data has been loaded to prevent overwriting user edits
  const initialDataLoadedRef = useRef(false);

  useEffect(() => {
    fetchConfig();
    fetchLanguages();
    fetchChallengesAndEvents();
    fetchCategories();
  }, []);

  // Fetch challenges and events for teaser dropdowns
  const fetchChallengesAndEvents = async () => {
    try {
      const [challengesRes, eventsRes] = await Promise.all([
        api.get('/challenges?limit=500'),
        api.get('/cms/events')
      ]);
      const challenges = challengesRes.data.challenges || [];
      setAllChallenges(challenges);
      setAllEvents(eventsRes.data.events || []);

      // Calculate challenge counts per category
      const counts = {};
      challenges.forEach(challenge => {
        const categoryId = challenge.category_id || challenge.category?.id;
        if (categoryId) {
          counts[categoryId] = (counts[categoryId] || 0) + 1;
        }
      });
      setCategoryCounts(counts);
    } catch (error) {
      console.error('Failed to fetch challenges/events:', error);
    }
  };

  // Fetch challenge categories
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await api.get('/challenge-categories?all=true');
      if (response.data.success) {
        setCategories(response.data.categories || []);
        setAvailableIcons(response.data.availableIcons || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Toggle category enabled status
  const handleToggleCategory = async (categoryId, currentEnabled) => {
    // Prevent disabling if challenges exist in this category
    const challengeCount = categoryCounts[categoryId] || 0;
    if (currentEnabled && challengeCount > 0) {
      alert(`Cannot disable this category: ${challengeCount} challenge${challengeCount > 1 ? 's' : ''} still assigned to it. Please reassign or delete those challenges first.`);
      return;
    }

    try {
      const response = await api.put(`/challenge-categories/${categoryId}`, {
        is_enabled: !currentEnabled
      });
      if (response.data.success) {
        setCategories(prev => prev.map(cat =>
          cat.id === categoryId ? { ...cat, isEnabled: !currentEnabled } : cat
        ));
      }
    } catch (error) {
      console.error('Failed to toggle category:', error);
      alert(error.response?.data?.error || 'Failed to update category');
    }
  };

  // Create custom category
  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      alert('Category name is required');
      return;
    }
    try {
      setSavingCategory(true);
      const response = await api.post('/challenge-categories', newCategory);
      if (response.data.success) {
        setCategories(prev => [...prev, response.data.category]);
        setNewCategory({ name: '', icon: 'map-pin', color: '#6B7280', markerIconUrl: '' });
      }
    } catch (error) {
      console.error('Failed to create category:', error);
      alert(error.response?.data?.error || 'Failed to create category');
    } finally {
      setSavingCategory(false);
    }
  };

  // Update custom category
  const handleUpdateCategory = async (categoryId, updates) => {
    try {
      const response = await api.put(`/challenge-categories/${categoryId}`, updates);
      if (response.data.success) {
        setCategories(prev => prev.map(cat =>
          cat.id === categoryId ? response.data.category : cat
        ));
      }
    } catch (error) {
      console.error('Failed to update category:', error);
      alert(error.response?.data?.error || 'Failed to update category');
    }
  };

  // Delete custom category
  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category? Challenges using it will be moved to General.')) {
      return;
    }
    try {
      const response = await api.delete(`/challenge-categories/${categoryId}`);
      if (response.data.success) {
        setCategories(prev => prev.filter(cat => cat.id !== categoryId));
      }
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert(error.response?.data?.error || 'Failed to delete category');
    }
  };

  // Upload marker icon for category
  const handleMarkerIconUpload = async (categoryId, file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a PNG, SVG, or WebP image (white silhouette on transparent background recommended)');
      return;
    }

    setUploadingMarkerIcon(categoryId);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('categoryId', categoryId);

      const uploadResponse = await api.post('/challenge-categories/upload/marker-icon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (uploadResponse.data.success && uploadResponse.data.url) {
        // Update local state with the new marker icon URL
        setCategories(prev => prev.map(cat =>
          cat.id === categoryId ? { ...cat, markerIconUrl: uploadResponse.data.url } : cat
        ));
      }
    } catch (error) {
      console.error('Failed to upload marker icon:', error);
      alert(error.response?.data?.error || 'Failed to upload marker icon');
    } finally {
      setUploadingMarkerIcon(null);
    }
  };

  // Remove marker icon from category
  const handleRemoveMarkerIcon = async (categoryId) => {
    try {
      await handleUpdateCategory(categoryId, { markerIconUrl: null });
    } catch (error) {
      console.error('Failed to remove marker icon:', error);
    }
  };

  // Fetch available languages for multi-language support
  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API_URL}/v1/translations/discover-flaeming/languages`);
      const data = response.data;
      setAvailableLanguages(data.languages || []);
      // Set default language
      const defaultLang = data.languages?.find(l => l.isDefault)?.code || 'en';
      setCurrentLanguage(defaultLang);
    } catch (error) {
      console.error('Failed to fetch languages:', error);
      // Fallback to English only
      setAvailableLanguages([{ code: 'en', name: 'English', nativeName: 'English', isDefault: true, enabled: true }]);
    }
  };

  // Add new language
  const handleAddLanguage = async () => {
    if (!newLanguageForm.code || !newLanguageForm.name) {
      alert('Language code and name are required');
      return;
    }
    try {
      setSavingLanguage(true);
      const token = localStorage.getItem('cms_accessToken');
      await axios.post(
        `${API_URL}/v1/translations/discover-flaeming/languages`,
        {
          languageCode: newLanguageForm.code.toLowerCase(),
          languageName: newLanguageForm.name,
          nativeName: newLanguageForm.nativeName || newLanguageForm.name,
          isDefault: false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchLanguages();
      setShowAddLanguageModal(false);
      setNewLanguageForm({ code: '', name: '', nativeName: '' });
      alert('Language added successfully!');
    } catch (error) {
      console.error('Error adding language:', error);
      alert('Failed to add language: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingLanguage(false);
    }
  };

  // Toggle language enabled/disabled
  const handleToggleLanguage = async (langCode, currentEnabled, isDefault) => {
    // Cannot disable default language
    if (isDefault && currentEnabled) {
      alert('Cannot disable the default language. Set another language as default first.');
      return;
    }
    try {
      const token = localStorage.getItem('cms_accessToken');
      await axios.put(
        `${API_URL}/v1/translations/discover-flaeming/languages/${langCode}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchLanguages();
    } catch (error) {
      console.error('Error toggling language:', error);
      alert('Failed to update language: ' + (error.response?.data?.error || error.message));
    }
  };

  // Set language as default
  const handleSetDefaultLanguage = async (langCode) => {
    try {
      const token = localStorage.getItem('cms_accessToken');
      await axios.put(
        `${API_URL}/v1/translations/discover-flaeming/languages/${langCode}/default`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchLanguages();
      alert(`${langCode.toUpperCase()} is now the default language`);
    } catch (error) {
      console.error('Error setting default language:', error);
      alert('Failed to set default language: ' + (error.response?.data?.error || error.message));
    }
  };


  // Map snake_case keys to camelCase config field names
  const keyToConfigField = {
    'login_title': 'loginTitle',
    'login_subtitle': 'loginSubtitle',
    'forgot_password': 'forgotPasswordText',
    'register_link': 'registerLinkText',
    'home_greeting': 'homeGreetingText',
    'powered_by': 'poweredByText'
  };

  // Helper function to get translation for current language
  const getTranslation = (key) => {
    const configKey = keyToConfigField[key] || key;
    // Use ?? (nullish coalescing) instead of || to allow empty strings
    // Only fall back if value is null/undefined, not if it's an empty string
    let value;
    const currentLangValue = translations[currentLanguage]?.[key];
    if (currentLangValue !== undefined && currentLangValue !== null) {
      value = currentLangValue;
    } else {
      const enValue = translations['en']?.[key];
      if (enValue !== undefined && enValue !== null) {
        value = enValue;
      } else {
        value = config?.[configKey] ?? '';
      }
    }
    // Convert actual newlines to literal \n for display in input fields
    return value ? value.replace(/\n/g, '\\n') : value;
  };

  // Helper function to set translation for current language
  const setTranslation = (key, value) => {
    setTranslations(prev => ({
      ...prev,
      [currentLanguage]: {
        ...prev[currentLanguage],
        [key]: value
      }
    }));
    // Also update config for immediate display (for default language)
    // Use the mapped camelCase key for config (e.g., login_title -> loginTitle)
    if (currentLanguage === 'en' || availableLanguages.find(l => l.code === currentLanguage && l.isDefault)) {
      const configKey = keyToConfigField[key] || key;
      setConfig(prev => ({ ...prev, [configKey]: value }));
    }
  };

  // Language Tabs Component
  const LanguageTabs = ({ languages, currentLang, onChangeLang }) => {
    const enabledLanguages = languages?.filter(l => l.enabled) || [];
    if (enabledLanguages.length <= 1) return null;

    return (
      <div className="flex space-x-1 border-b border-gray-200 mb-4">
        {enabledLanguages.map(lang => (
          <button
            key={lang.code}
            onClick={() => onChangeLang(lang.code)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              currentLang === lang.code
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            {lang.nativeName}
            {lang.isDefault && <span className="ml-1 text-xs opacity-70">(default)</span>}
          </button>
        ))}
      </div>
    );
  };

  const fetchConfig = async (forceReload = false) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/v1/app-config/discover-flaeming`);
      const data = response.data;

      // Only set config/translations on initial load (or force reload) to prevent overwriting user edits
      const shouldUpdateState = !initialDataLoadedRef.current || forceReload;
      if (shouldUpdateState) {
        setConfig(data.app);
      }

      // Set image previews from existing URLs
      if (data.app.splashLogoUrl) {
        setSplashLogoPreview(data.app.splashLogoUrl);
      }
      if (data.app.splashBackgroundImageUrl) {
        setSplashBgPreview(data.app.splashBackgroundImageUrl);
      }
      if (data.app.appBackgroundImageUrl) {
        setAppBgImagePreview(data.app.appBackgroundImageUrl);
      }
      if (data.app.loginBackgroundImageUrl) {
        setLoginBgPreview(data.app.loginBackgroundImageUrl);
      }
      if (data.app.poweredByLogoUrl) {
        setPoweredByLogoPreview(data.app.poweredByLogoUrl);
      }
      if (data.app.mapSectionBackgroundImageUrl) {
        setMapSectionBgPreview(data.app.mapSectionBackgroundImageUrl);
      }
      if (data.app.achievementsSectionBackgroundImageUrl) {
        setAchievementsSectionBgPreview(data.app.achievementsSectionBackgroundImageUrl);
      }
      if (data.app.settingsSectionBackgroundImageUrl) {
        setSettingsSectionBgPreview(data.app.settingsSectionBackgroundImageUrl);
      }

      // Only update editable state on initial load or force reload to prevent overwriting user edits
      if (shouldUpdateState) {
        // Convert objects to arrays for easier rendering
        // Parse new nested color structure
        if (data.colors) {
          if (data.colors.light && data.colors.dark) {
            // New structure with light/dark themes
            setLightColors(data.colors.light || {});
            setDarkColors(data.colors.dark || {});
          } else {
            // Old structure (backward compatibility)
            setColors(Object.entries(data.colors).map(([key, value]) => ({ key, value })));
          }
        }

        // Parse screen backgrounds
        if (data.screens) {
          setScreenBackgrounds(data.screens);
        }

        // Parse onboarding data
        if (data.onboarding) {
          setOnboarding(data.onboarding);
        }

        setTypography(Object.entries(data.typography).map(([key, value]) => ({
          key,
          ...value
        })));
        setFeatures(Object.entries(data.features).map(([key, value]) => ({
          key,
          ...value
        })));
        setStrings(Object.entries(data.strings).map(([key, value]) => ({
          key,
          value,
          locale: 'en'
        })));

        // Load teasers with translation initialization
        if (data.teasers) {
          const teasersWithTranslations = data.teasers.map(t => ({
            ...t,
            translations: t.translations || {
              en: {
                title: t.title || '',
                subtitle: t.subtitle || '',
                description: t.description || '',
                cta_text: t.ctaText || ''
              }
            }
          }));
          setTeasers(teasersWithTranslations);
        }

        // Load existing translations or initialize from config values
        if (data.translations) {
          setTranslations(data.translations);
        } else {
          // Initialize translations with current config values for default language
          const defaultTranslations = {
            en: {
              loginTitle: data.app.loginTitle || '',
              loginSubtitle: data.app.loginSubtitle || '',
              forgotPasswordText: data.app.forgotPasswordText || 'Forgot Password?',
              registerLinkText: data.app.registerLinkText || "Don't have an account? Register",
              homeGreetingText: data.app.homeGreetingText || '',
              poweredByText: data.app.poweredByText || 'Powered by'
            }
          };
          setTranslations(defaultTranslations);
        }

        // Load share settings
        if (data.share) {
          setShareSettings(data.share);
        }

        // Mark initial load as complete
        initialDataLoadedRef.current = true;
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching app config:', error);
      setLoading(false);
    }
  };

  const handleImageUpload = async (event, imageType) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('deploymentKey', 'discover-flaeming');
      formData.append('type', imageType);

      const token = localStorage.getItem('cms_accessToken');
      const response = await axios.post(
        `${API_URL}/v1/app-assets/upload?deploymentKey=discover-flaeming`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
            // Note: Don't manually set Content-Type for FormData
            // Axios/browser will automatically set it with proper boundary
          }
        }
      );

      if (response.data.success && response.data.url) {
        if (imageType === 'splash_logo') {
          setConfig({ ...config, splashLogoUrl: response.data.url });
          setSplashLogoPreview(response.data.url);
        } else if (imageType === 'splash_bg') {
          setConfig({ ...config, splashBackgroundImageUrl: response.data.url });
          setSplashBgPreview(response.data.url);
        } else if (imageType === 'app_background') {
          setConfig({ ...config, appBackgroundImageUrl: response.data.url });
          setAppBgImagePreview(response.data.url);
        } else if (imageType === 'login_bg') {
          setConfig({ ...config, loginBackgroundImageUrl: response.data.url });
          setLoginBgPreview(response.data.url);
        } else if (imageType === 'powered_by_logo') {
          setConfig({ ...config, poweredByLogoUrl: response.data.url });
          setPoweredByLogoPreview(response.data.url);
        } else if (imageType === 'map_section_bg') {
          setConfig({ ...config, mapSectionBackgroundImageUrl: response.data.url });
          setMapSectionBgPreview(response.data.url);
        } else if (imageType === 'achievements_section_bg') {
          setConfig({ ...config, achievementsSectionBackgroundImageUrl: response.data.url });
          setAchievementsSectionBgPreview(response.data.url);
        } else if (imageType === 'settings_section_bg') {
          setConfig({ ...config, settingsSectionBackgroundImageUrl: response.data.url });
          setSettingsSectionBgPreview(response.data.url);
        }
        alert('Image uploaded successfully!');
      } else {
        alert('Upload failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
      if (error.response?.status === 413) {
        alert('Upload failed: File is too large. Maximum size is 10MB.');
      } else if (error.response?.status === 403) {
        alert('Upload failed: Permission denied. Only admins can upload images.');
      } else if (error.response?.status === 400) {
        alert('Upload failed: ' + errorMsg);
      } else {
        alert('Upload failed: ' + errorMsg);
      }
    } finally {
      setUploading(false);
    }
  };

  // Handle opening crop modal for onboarding images
  const openCropModal = (imageUrl, screenIndex, mode) => {
    setCropModal({
      isOpen: true,
      imageUrl,
      screenIndex,
      mode
    });
    setCrop({ unit: '%', width: 80, height: 80, x: 10, y: 10 });
    setCompletedCrop(null);
  };

  // Handle saving cropped image
  const handleCropSave = async () => {
    if (!completedCrop || !cropModal.imageUrl) {
      setCropModal({ isOpen: false, imageUrl: '', screenIndex: null, mode: null });
      return;
    }

    // Get displayed image dimensions (what ReactCrop uses for pixel values)
    const displayedWidth = cropImgRef.current?.width || 1;
    const displayedHeight = cropImgRef.current?.height || 1;

    console.log('Crop Debug:', {
      completedCrop,
      displayedWidth, displayedHeight,
      naturalWidth: cropImgRef.current?.naturalWidth,
      naturalHeight: cropImgRef.current?.naturalHeight
    });

    // Convert crop to percentages
    // ReactCrop pixel values are relative to displayed image size
    // Percentages will work correctly on any size since backend applies to original
    let cropData;
    if (completedCrop.unit === 'px') {
      // Convert pixels (relative to displayed size) to percentages
      cropData = {
        x: (completedCrop.x / displayedWidth) * 100,
        y: (completedCrop.y / displayedHeight) * 100,
        width: (completedCrop.width / displayedWidth) * 100,
        height: (completedCrop.height / displayedHeight) * 100
      };
    } else {
      // Already in percentage
      cropData = {
        x: completedCrop.x,
        y: completedCrop.y,
        width: completedCrop.width,
        height: completedCrop.height
      };
    }

    console.log('Crop Data (percentages):', cropData);

    setCroppingSaving(true);
    try {
      const token = localStorage.getItem('cms_accessToken');
      const response = await axios.post(`${API_URL}/cms/crop-image`, {
        imageUrl: cropModal.imageUrl,
        cropData: cropData
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        // Update onboarding state with new cropped image URL
        const newScreens = [...onboarding.screens];
        newScreens[cropModal.screenIndex] = {
          ...newScreens[cropModal.screenIndex],
          backgroundImage: {
            ...newScreens[cropModal.screenIndex].backgroundImage,
            [cropModal.mode]: response.data.imageUrl
          }
        };
        const updatedOnboarding = { ...onboarding, screens: newScreens };
        setOnboarding(updatedOnboarding);

        // Auto-save the cropped image URL to database
        try {
          const saveResponse = await axios.put(
            `${API_URL}/v1/app-config/discover-flaeming`,
            { onboarding: updatedOnboarding },
            { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
          );
          if (saveResponse.data.success) {
            alert('Image cropped and saved successfully!');
          } else {
            alert('Image cropped but failed to save to database. Please click "Save Changes".');
          }
        } catch (saveError) {
          console.error('Error auto-saving crop:', saveError);
          alert('Image cropped but failed to save. Please click "Save Changes" to persist.');
        }
      } else {
        alert('Crop failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error cropping image:', error);
      alert('Crop failed: ' + error.message);
    } finally {
      setCroppingSaving(false);
      setCropModal({ isOpen: false, imageUrl: '', screenIndex: null, mode: null });
    }
  };

  // Crop presets
  const cropPresets = [
    { name: 'Full', crop: { unit: '%', width: 100, height: 100, x: 0, y: 0 } },
    { name: 'Center Square', crop: { unit: '%', width: 80, height: 80, x: 10, y: 10 } },
    { name: 'Portrait 9:16', crop: { unit: '%', width: 56, height: 100, x: 22, y: 0 } },
    { name: 'Top', crop: { unit: '%', width: 100, height: 50, x: 0, y: 0 } },
    { name: 'Center', crop: { unit: '%', width: 100, height: 50, x: 0, y: 25 } },
    { name: 'Bottom', crop: { unit: '%', width: 100, height: 50, x: 0, y: 50 } }
  ];

  const handleSave = async () => {
    try {
      setSaving(true);

      const token = localStorage.getItem('cms_accessToken');
      if (!token) {
        alert('Authentication required. Please log in again.');
        return;
      }

      const updateData = {
        app: {
          name: config.name,
          tagline: config.tagline,
          version: config.version,
          versionCode: config.versionCode,
          apiBaseUrl: config.apiBaseUrl,
          supportEmail: config.supportEmail || '',
          splashLogoUrl: config.splashLogoUrl || '',
          splashBackgroundImageUrl: config.splashBackgroundImageUrl || '',
          appBackgroundImageUrl: config.appBackgroundImageUrl || '',
          loginBackgroundImageUrl: config.loginBackgroundImageUrl || '',
          loginTitle: config.loginTitle || '',
          loginSubtitle: config.loginSubtitle || '',
          poweredByLogoUrl: config.poweredByLogoUrl || '',
          showPoweredBy: config.showPoweredBy !== undefined ? config.showPoweredBy : true,
          buttonPrimaryColor: config.buttonPrimaryColor || '',
          buttonSecondaryColor: config.buttonSecondaryColor || '',
          inputFieldBackgroundColor: config.inputFieldBackgroundColor || '',
          inputFieldTextColor: config.inputFieldTextColor || '',
          inputFieldBorderColor: config.inputFieldBorderColor || "#78350F",
          inputFieldBorderWidth: config.inputFieldBorderWidth || 2,
          forgotPasswordText: config.forgotPasswordText || 'Forgot Password?',
          forgotPasswordTextColor: config.forgotPasswordTextColor || '#DC2626',
          registerLinkText: config.registerLinkText || "Don't have an account? Register",
          registerLinkTextColor: config.registerLinkTextColor || '#000000',
          poweredByText: config.poweredByText || 'Powered by',
          poweredByTextColor: config.poweredByTextColor || '#FFFFFF',
          homeGreetingText: config.homeGreetingText || 'Welcome to Fläming, Hans!',
          mapSectionBackgroundColor: config.mapSectionBackgroundColor || '',
          mapSectionBackgroundImageUrl: config.mapSectionBackgroundImageUrl || '',
          achievementsSectionBackgroundColor: config.achievementsSectionBackgroundColor || '',
          achievementsSectionBackgroundImageUrl: config.achievementsSectionBackgroundImageUrl || '',
          settingsSectionBackgroundColor: config.settingsSectionBackgroundColor || '',
          settingsSectionBackgroundImageUrl: config.settingsSectionBackgroundImageUrl || '',
          map: {
            defaultLatitude: config.map?.defaultLatitude || null,
            defaultLongitude: config.map?.defaultLongitude || null,
            defaultZoom: config.map?.defaultZoom || 13,
            defaultRadius: config.map?.defaultRadius || 10000
          }
        },
        colors: {
          light: lightColors,
          dark: darkColors
        },
        screens: screenBackgrounds,
        typography: {},
        features: {},
        strings: {}
      };

      // Keep old colors array structure for backward compatibility (if colors array exists)
      if (colors && colors.length > 0) {
        colors.forEach(color => {
          updateData.colors[color.key] = color.value;
        });
      }

      typography.forEach(font => {
        updateData.typography[font.key] = {
          fontFamily: font.fontFamily,
          fontFamilyAndroid: font.fontFamilyAndroid || font.fontFamily,
          fontFamilyIos: font.fontFamilyIos || font.fontFamily,
          fontWeight: font.fontWeight,
          fontSize: font.fontSize,
          lineHeight: font.lineHeight,
          fontColor: font.fontColor || 'text_primary'
        };
      });

      features.forEach(feature => {
        updateData.features[feature.key] = {
          enabled: feature.enabled,
          config: feature.config || null
        };
      });

      strings.forEach(str => {
        updateData.strings[str.key] = str.value;
      });

      // Add translations for multi-language support
      // Process translations to convert literal \n to actual newlines
      const processedTranslations = {};
      Object.entries(translations).forEach(([locale, strings]) => {
        processedTranslations[locale] = {};
        Object.entries(strings || {}).forEach(([key, value]) => {
          // Convert literal \n to actual newlines
          processedTranslations[locale][key] = value ? value.replace(/\\n/g, '\n') : value;
        });
      });
      updateData.translations = processedTranslations;

      // Add onboarding data
      updateData.onboarding = onboarding;

      // Add teasers
      updateData.teasers = teasers;

      const response = await axios.put(
        `${API_URL}/v1/app-config/discover-flaeming`,
        updateData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        alert('Configuration saved successfully! Changes will be reflected in the mobile app.');
      } else {
        alert('Save failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.response.data?.error;

        if (status === 401) {
          alert('Authentication expired. Please log in again.');
        } else if (status === 403) {
          alert('Permission denied. Only admins can save configuration.');
        } else {
          alert('Error: ' + message);
        }
      } else {
        alert('Network error: ' + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleColorChange = (index, value) => {
    const newColors = [...colors];
    newColors[index].value = value;
    setColors(newColors);
  };

  const handleFeatureToggle = (index) => {
    const newFeatures = [...features];
    newFeatures[index].enabled = !newFeatures[index].enabled;
    setFeatures(newFeatures);
  };

  const handleFeatureConfigChange = (index, field, value) => {
    const newFeatures = [...features];
    if (!newFeatures[index].config) newFeatures[index].config = {};
    newFeatures[index].config[field] = value;
    setFeatures(newFeatures);
  };

  const renderColorInput = (label, value, onChange, description = '', dbField = '', isDark = false) => {
    return (
      <div className={`border rounded-lg p-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
          {label}
          {dbField && <span className="text-xs font-normal ml-1 text-gray-400">({dbField})</span>}
          {description && <span className={`text-xs block mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{description}</span>}
        </label>
        <div className="flex gap-2">
          <input
            type="color"
            value={value || '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className={`w-16 h-10 border rounded cursor-pointer ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
          />
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#000000"
            className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300'}`}
          />
        </div>
        <div
          className={`mt-2 h-8 rounded border ${isDark ? 'border-gray-600' : 'border-gray-300'}`}
          style={{ backgroundColor: value || '#FFFFFF' }}
        />
      </div>
    );
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'basic', label: t('cms.appSettings.general'), icon: FiPackage },
    { id: 'onboarding', label: 'Onboarding', icon: FiLayers },
    { id: 'teasers', label: t('cms.appSettings.teasers'), icon: FiStar },
    { id: 'categories', label: t('cms.appSettings.categories'), icon: FiGrid },
    { id: 'share', label: t('cms.appSettings.sharing'), icon: FiShare2 },
    { id: 'colors', label: t('cms.appSettings.colors'), icon: FiEye },
    { id: 'typography', label: 'Typography', icon: FiType },
    { id: 'features', label: t('cms.appSettings.features'), icon: FiToggleLeft },
    { id: 'strings', label: t('cms.appSettings.translations'), icon: FiGlobe },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">{t('cms.nav.appSettings')}</h1>
          <p className="text-gray-600">{t('cms.appSettings.subtitle')}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex gap-3">
        <button
          onClick={handleSave}
          disabled={saving || uploading}
          className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          <FiSave />
          {saving ? t('cms.common.loading') : t('cms.appSettings.saveSettings')}
        </button>
        <button
          onClick={() => fetchConfig(true)}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
        >
          <FiRefreshCw />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && config && (
          <div className="space-y-6">
            {/* Language Management Section - FIRST */}
            <div className="rounded-xl p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <FiGlobe className="w-6 h-6 text-primary-600" />
                  <h2 className="text-xl font-bold text-gray-900">Language Settings</h2>
                </div>
                <button
                  onClick={() => setShowAddLanguageModal(true)}
                  className="btn btn-primary flex items-center gap-2 transition-colors"
                >
                  <FiPlus className="w-4 h-4" />
                  Add Language
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Configure which languages are available in the app. Users will be able to choose from enabled languages.
              </p>

              {/* Languages Table */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Language</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Code</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Native Name</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Default</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Enabled</th>

                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {availableLanguages.map((lang) => (
                      <tr key={lang.code} className={!lang.enabled ? 'bg-gray-50 opacity-60' : ''}>
                        <td className="px-4 py-3 font-medium text-gray-900">{lang.name}</td>
                        <td className="px-4 py-3 text-gray-600 font-mono text-sm">{lang.code}</td>
                        <td className="px-4 py-3 text-gray-600">{lang.nativeName}</td>
                        <td className="px-4 py-3 text-center">
                          {lang.isDefault ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                              <FiStar className="w-3 h-3 fill-current" /> Default
                            </span>
                          ) : (
                            <button
                              onClick={() => handleSetDefaultLanguage(lang.code)}
                              disabled={!lang.enabled}
                              className="text-gray-400 hover:text-yellow-600 transition-colors disabled:cursor-not-allowed"
                              title="Set as default"
                            >
                              <FiStar className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleToggleLanguage(lang.code, lang.enabled, lang.isDefault)}
                            disabled={lang.isDefault && lang.enabled}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              lang.enabled
                                ? 'bg-primary-500'
                                : 'bg-gray-300'
                            } ${lang.isDefault && lang.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                            title={lang.isDefault && lang.enabled ? 'Cannot disable default language' : (lang.enabled ? 'Click to disable' : 'Click to enable')}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                lang.enabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </td>

                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {availableLanguages.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No languages configured. Click "Add Language" to get started.
                </div>
              )}
            </div>

            {/* Add Language Modal */}
            {showAddLanguageModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Add New Language</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Language Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newLanguageForm.code}
                        onChange={(e) => setNewLanguageForm({...newLanguageForm, code: e.target.value})}
                        placeholder="e.g., fr, es, ar"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        maxLength={5}
                      />
                      <p className="text-xs text-gray-500 mt-1">ISO 639-1 code (e.g., en, de, fr, es, ar, zh)</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Language Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newLanguageForm.name}
                        onChange={(e) => setNewLanguageForm({...newLanguageForm, name: e.target.value})}
                        placeholder="e.g., French, Spanish, Arabic"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Native Name
                      </label>
                      <input
                        type="text"
                        value={newLanguageForm.nativeName}
                        onChange={(e) => setNewLanguageForm({...newLanguageForm, nativeName: e.target.value})}
                        placeholder="e.g., Français, Español, العربية"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">How the language is written in itself</p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => {
                        setShowAddLanguageModal(false);
                        setNewLanguageForm({ code: '', name: '', nativeName: '' });
                      }}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      {t('cms.common.cancel')}
                    </button>
                    <button
                      onClick={handleAddLanguage}
                      disabled={savingLanguage || !newLanguageForm.code || !newLanguageForm.name}
                      className="btn btn-primary flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingLanguage ? (
                        <>
                          <FiRefreshCw className="w-4 h-4 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <FiPlus className="w-4 h-4" />
                          Add Language
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">App Information</h2>
              {availableLanguages.filter(l => l.enabled).length > 1 && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <FiGlobe className="w-4 h-4" />
                  <span>Multi-language</span>
                </div>
              )}
            </div>
            <LanguageTabs
              languages={availableLanguages}
              currentLang={currentLanguage}
              onChangeLang={setCurrentLanguage}
            />
            {availableLanguages.filter(l => l.enabled).length > 1 && (
              <p className="text-xs text-primary-600 bg-primary-50 p-2 rounded mb-4">
                Editing: <strong>{availableLanguages.find(l => l.code === currentLanguage)?.nativeName || currentLanguage}</strong>
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('cms.appSettings.appName')} <span className="text-xs font-normal text-gray-400">(app_name)</span>
                </label>
                <input
                  type="text"
                  value={getTranslation('app_name') || config.name}
                  onChange={(e) => setTranslation('app_name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={config.name}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('cms.appSettings.tagline')} <span className="text-xs font-normal text-gray-400">(app_tagline)</span>
                </label>
                <input
                  type="text"
                  value={getTranslation('app_tagline') || config.tagline}
                  onChange={(e) => setTranslation('app_tagline', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={config.tagline}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Package Name <span className="text-xs font-normal text-gray-400">(package_name)</span>
                </label>
                <input
                  type="text"
                  value={config.packageName}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Package name cannot be changed</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Version <span className="text-xs font-normal text-gray-400">(app_version, version_code)</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={config.version}
                    onChange={(e) => setConfig({...config, version: e.target.value})}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="1.0.0"
                  />
                  <input
                    type="number"
                    value={config.versionCode}
                    onChange={(e) => setConfig({...config, versionCode: parseInt(e.target.value)})}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="1"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Base URL <span className="text-xs font-normal text-gray-400">(api_base_url)</span>
                </label>
                <input
                  type="url"
                  value={config.apiBaseUrl}
                  onChange={(e) => setConfig({...config, apiBaseUrl: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Support Email <span className="text-xs font-normal text-gray-400">(support_email)</span>
                </label>
                <input
                  type="email"
                  value={config.supportEmail || ''}
                  onChange={(e) => setConfig({...config, supportEmail: e.target.value})}
                  placeholder="support@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Users can send support requests from the app to this email</p>
              </div>

              {/* Translatable Texts Section */}
              <div className="md:col-span-2 border-t pt-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Translatable Texts</h3>
                  {availableLanguages.filter(l => l.enabled).length > 1 && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <FiGlobe className="w-4 h-4" />
                      <span>Multi-language enabled</span>
                    </div>
                  )}
                </div>
                <LanguageTabs
                  languages={availableLanguages}
                  currentLang={currentLanguage}
                  onChangeLang={setCurrentLanguage}
                />
                {availableLanguages.filter(l => l.enabled).length > 1 && (
                  <p className="text-xs text-primary-600 bg-primary-50 p-2 rounded mb-4">
                    Editing translations for: <strong>{availableLanguages.find(l => l.code === currentLanguage)?.nativeName || currentLanguage}</strong>
                    {currentLanguage !== 'en' && ' (non-default language)'}
                  </p>
                )}
              </div>

              {/* Login Screen Texts */}
              <div className="md:col-span-2">
                <h4 className="text-md font-semibold text-gray-800 mb-3 border-b pb-2">Login Screen</h4>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Login Title <span className="text-xs font-normal text-gray-400">(login_title)</span>
                </label>
                <input
                  type="text"
                  value={getTranslation('login_title')}
                  onChange={(e) => setTranslation('login_title', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Welcome to Discover Fläming"
                />
                <p className="text-xs text-gray-500 mt-1">Displayed as centered headline on login screen</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Login Subtitle <span className="text-xs font-normal text-gray-400">(login_subtitle)</span>
                </label>
                <input
                  type="text"
                  value={getTranslation('login_subtitle')}
                  onChange={(e) => setTranslation('login_subtitle', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Explore the autumn beauty"
                />
                <p className="text-xs text-gray-500 mt-1">Displayed below login title</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Forgot Password Text <span className="text-xs font-normal text-gray-400">(forgot_password_text)</span>
                </label>
                <input
                  type="text"
                  value={getTranslation('forgot_password')}
                  onChange={(e) => setTranslation('forgot_password', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Forgot Password?"
                />
                <p className="text-xs text-gray-500 mt-1">Link text for password recovery</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Register Link Text <span className="text-xs font-normal text-gray-400">(register_link_text)</span>
                </label>
                <input
                  type="text"
                  value={getTranslation('register_link')}
                  onChange={(e) => setTranslation('register_link', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Don't have an account? Register"
                />
                <p className="text-xs text-gray-500 mt-1">Link text for user registration</p>
              </div>

              {/* Home Screen Texts */}
              <div className="md:col-span-2 mt-4">
                <h4 className="text-md font-semibold text-gray-800 mb-3 border-b pb-2">Home Screen</h4>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Welcome Greeting <span className="text-xs font-normal text-gray-400">(home_greeting_text)</span>
                </label>
                <input
                  type="text"
                  value={getTranslation('home_greeting')}
                  onChange={(e) => setTranslation('home_greeting', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Welcome to the wild Fläming, {username}!"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Displayed at the top of the home screen. Use <code className="bg-gray-100 px-1 rounded">{'{username}'}</code> as a placeholder for the user's name.
                </p>
              </div>

              {/* Powered By Section */}
              <div className="md:col-span-2 mt-4">
                <h4 className="text-md font-semibold text-gray-800 mb-3 border-b pb-2">Powered By</h4>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Powered By Text <span className="text-xs font-normal text-gray-400">(powered_by_text)</span>
                </label>
                <input
                  type="text"
                  value={getTranslation('powered_by')}
                  onChange={(e) => setTranslation('powered_by', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Powered by"
                />
                <p className="text-xs text-gray-500 mt-1">Text displayed above the powered by logo</p>
              </div>

              {/* Image Uploads Section */}
              <div className="md:col-span-2 border-t pt-6 mt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FiImage className="w-5 h-5" />
                  App Assets
                </h3>
              </div>

              {/* Splash Screen Logo */}
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Splash Screen Logo <span className="text-xs font-normal text-gray-400">(splash_logo_url)</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  {splashLogoPreview && (
                    <div className="mb-4">
                      <img
                        src={splashLogoPreview}
                        alt="Splash logo preview"
                        className="h-32 mx-auto object-contain mb-3 rounded"
                      />
                      <p className="text-xs text-gray-500 text-center truncate">
                        {config.splashLogoUrl}
                      </p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'splash_logo')}
                    disabled={uploading}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Recommended: PNG, max 5MB
                  </p>
                </div>
              </div>

              {/* Powered By Logo */}
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Powered By Logo <span className="text-xs font-normal text-gray-400">(powered_by_logo_url)</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  {poweredByLogoPreview && (
                    <div className="mb-4">
                      <img
                        src={poweredByLogoPreview}
                        alt="Powered by logo preview"
                        className="h-20 mx-auto object-contain mb-3 rounded"
                      />
                      <p className="text-xs text-gray-500 text-center truncate">
                        {config.poweredByLogoUrl}
                      </p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'powered_by_logo')}
                    disabled={uploading}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Recommended: PNG with transparency, max 5MB
                  </p>
                </div>
              </div>

              {/* Login Background Image */}
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Login Background Image <span className="text-xs font-normal text-gray-400">(login_bg_image_url)</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  {loginBgPreview && (
                    <div className="mb-4 flex flex-col items-center">
                      <img
                        src={loginBgPreview}
                        alt="Login background preview"
                        className="w-24 h-48 object-cover mb-3 rounded-lg border shadow-sm"
                      />
                      <p className="text-xs text-gray-500 text-center truncate">
                        {config.loginBackgroundImageUrl}
                      </p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'login_bg')}
                    disabled={uploading}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Recommended: High-res image, max 5MB
                  </p>
                </div>
              </div>

              {/* Show Powered By Toggle */}
              <div className="md:col-span-2 border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Show "Powered By" Section <span className="text-xs font-normal text-gray-400">(show_powered_by)</span>
                    </label>
                    <p className="text-xs text-gray-500">
                      Display powered by section on splash and login screens
                    </p>
                  </div>
                  <button
                    onClick={() => setConfig({...config, showPoweredBy: !config.showPoweredBy})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      config.showPoweredBy ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        config.showPoweredBy ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Map Default Location */}
              <div className="md:col-span-2 border-t pt-6 mt-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <FiMapPin className="w-5 h-5 text-primary-600" />
                  Map Default Location
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Set the default center point when the map first opens in the app
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Latitude <span className="text-xs font-normal text-gray-400">(map_default_latitude)</span>
                </label>
                <input
                  type="number"
                  step="0.00001"
                  value={config?.map?.defaultLatitude || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    map: {
                      ...config?.map,
                      defaultLatitude: e.target.value ? parseFloat(e.target.value) : null
                    }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 52.14223"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Longitude <span className="text-xs font-normal text-gray-400">(map_default_longitude)</span>
                </label>
                <input
                  type="number"
                  step="0.00001"
                  value={config?.map?.defaultLongitude || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    map: {
                      ...config?.map,
                      defaultLongitude: e.target.value ? parseFloat(e.target.value) : null
                    }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 12.59187"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Zoom Level <span className="text-xs font-normal text-gray-400">(map_default_zoom)</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={config?.map?.defaultZoom || 13}
                  onChange={(e) => setConfig({
                    ...config,
                    map: {
                      ...config?.map,
                      defaultZoom: parseInt(e.target.value) || 13
                    }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="13"
                />
                <p className="text-xs text-gray-500 mt-1">1 = world view, 20 = street level (default: 13)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Radius (meters) <span className="text-xs font-normal text-gray-400">(map_default_radius)</span>
                </label>
                <input
                  type="number"
                  min="100"
                  max="100000"
                  step="100"
                  value={config?.map?.defaultRadius || 10000}
                  onChange={(e) => setConfig({
                    ...config,
                    map: {
                      ...config?.map,
                      defaultRadius: parseInt(e.target.value) || 10000
                    }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="10000"
                />
                <p className="text-xs text-gray-500 mt-1">Search radius for nearby challenges in meters (default: 10000 = 10km)</p>
              </div>
            </div>
          </div>
        )}

{/* Onboarding Tab */}
        {activeTab === 'onboarding' && (
          <div className="space-y-6">
            <div className="rounded-xl p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <FiLayers className="w-6 h-6 text-gray-600" />
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Onboarding Carousel</h2>
                  <p className="text-sm text-gray-600">Configure the 3-screen onboarding carousel shown to new users</p>
                </div>
              </div>

              {/* Global Toggles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                  <div>
                    <label className="font-medium text-gray-900">Enable Onboarding <span className="text-xs font-normal text-gray-400">(onboarding_enabled)</span></label>
                    <p className="text-sm text-gray-500">Show carousel to new users</p>
                  </div>
                  <button
                    onClick={() => setOnboarding({...onboarding, enabled: !onboarding.enabled})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      onboarding.enabled ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      onboarding.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                  <div>
                    <label className="font-medium text-gray-900">Show Skip Button <span className="text-xs font-normal text-gray-400">(onboarding_skip_enabled)</span></label>
                    <p className="text-sm text-gray-500">Allow users to skip onboarding</p>
                  </div>
                  <button
                    onClick={() => setOnboarding({...onboarding, skipEnabled: !onboarding.skipEnabled})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      onboarding.skipEnabled ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      onboarding.skipEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Onboarding Screens */}
            {[0, 1, 2].map((screenIndex) => (
              <div key={screenIndex} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-bold">
                    {screenIndex + 1}
                  </span>
                  Screen {screenIndex + 1}
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left: Background Images */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-700">Background Images</h4>

                    {/* Light Mode Background */}
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Light Mode Background <span className="text-xs font-normal text-gray-400">(onboarding_{screenIndex + 1}_bg_image_light_url)</span>
                      </label>
                      {onboarding.screens[screenIndex]?.backgroundImage?.light && (
                        <div className="mb-3 relative group flex justify-center">
                          <img
                            src={onboarding.screens[screenIndex].backgroundImage.light}
                            alt={`Screen ${screenIndex + 1} light bg`}
                            className="w-28 h-56 object-cover rounded-xl border shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setImagePreview({
                              isOpen: true,
                              url: onboarding.screens[screenIndex].backgroundImage.light,
                              title: `Screen ${screenIndex + 1} - Light Mode`
                            })}
                            title="Click to preview"
                          />
                          <div className="absolute top-2 right-2 flex gap-1">
                            <button
                              type="button"
                              onClick={() => openCropModal(
                                onboarding.screens[screenIndex].backgroundImage.light,
                                screenIndex,
                                'light'
                              )}
                              className="bg-white/90 hover:bg-white text-gray-700 p-1.5 rounded-lg shadow-md flex items-center gap-1 text-xs font-medium"
                              title="Crop image"
                            >
                              <FiCrop className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm('Remove this onboarding image?')) {
                                  const newScreens = [...onboarding.screens];
                                  newScreens[screenIndex] = {
                                    ...newScreens[screenIndex],
                                    backgroundImage: {
                                      ...newScreens[screenIndex].backgroundImage,
                                      light: null
                                    }
                                  };
                                  setOnboarding({...onboarding, screens: newScreens});
                                }
                              }}
                              className="bg-red-500/90 hover:bg-red-500 text-white p-1.5 rounded-lg shadow-md flex items-center text-xs font-medium"
                              title="Remove image"
                            >
                              <FiTrash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setUploading(true);
                          try {
                            const token = localStorage.getItem('cms_accessToken');
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('type', `onboarding_${screenIndex + 1}_light`);
                            formData.append('deploymentKey', 'discover-flaeming');
                            const response = await axios.post(
                              `${API_URL}/v1/app-assets/upload?deploymentKey=discover-flaeming`,
                              formData,
                              { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
                            );
                            if (response.data.success && response.data.url) {
                              const newScreens = [...onboarding.screens];
                              newScreens[screenIndex] = {
                                ...newScreens[screenIndex],
                                backgroundImage: {
                                  ...newScreens[screenIndex].backgroundImage,
                                  light: response.data.url
                                }
                              };
                              setOnboarding({...onboarding, screens: newScreens});
                            }
                          } catch (error) {
                            const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
                            if (error.response?.status === 413) {
                              alert('Upload failed: File is too large. Maximum size is 10MB.');
                            } else {
                              alert('Upload failed: ' + errorMsg);
                            }
                          } finally {
                            setUploading(false);
                          }
                        }}
                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                      />
                    </div>

                    {/* Dark Mode Background */}
                    <div className="border border-gray-700 rounded-lg p-4 bg-gray-800">
                      <label className="block text-sm font-medium text-gray-200 mb-2">
                        Dark Mode Background <span className="text-xs font-normal text-gray-500">(onboarding_{screenIndex + 1}_bg_image_dark_url)</span>
                      </label>
                      {onboarding.screens[screenIndex]?.backgroundImage?.dark && (
                        <div className="mb-3 relative group flex justify-center">
                          <img
                            src={onboarding.screens[screenIndex].backgroundImage.dark}
                            alt={`Screen ${screenIndex + 1} dark bg`}
                            className="w-28 h-56 object-cover rounded-xl border border-gray-600 shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setImagePreview({
                              isOpen: true,
                              url: onboarding.screens[screenIndex].backgroundImage.dark,
                              title: `Screen ${screenIndex + 1} - Dark Mode`
                            })}
                            title="Click to preview"
                          />
                          <div className="absolute top-2 right-2 flex gap-1">
                            <button
                              type="button"
                              onClick={() => openCropModal(
                                onboarding.screens[screenIndex].backgroundImage.dark,
                                screenIndex,
                                'dark'
                              )}
                              className="bg-gray-900/90 hover:bg-gray-900 text-white p-1.5 rounded-lg shadow-md flex items-center gap-1 text-xs font-medium"
                              title="Crop image"
                            >
                              <FiCrop className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm('Remove this onboarding image?')) {
                                  const newScreens = [...onboarding.screens];
                                  newScreens[screenIndex] = {
                                    ...newScreens[screenIndex],
                                    backgroundImage: {
                                      ...newScreens[screenIndex].backgroundImage,
                                      dark: null
                                    }
                                  };
                                  setOnboarding({...onboarding, screens: newScreens});
                                }
                              }}
                              className="bg-red-500/90 hover:bg-red-500 text-white p-1.5 rounded-lg shadow-md flex items-center text-xs font-medium"
                              title="Remove image"
                            >
                              <FiTrash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setUploading(true);
                          try {
                            const token = localStorage.getItem('cms_accessToken');
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('type', `onboarding_${screenIndex + 1}_dark`);
                            formData.append('deploymentKey', 'discover-flaeming');
                            const response = await axios.post(
                              `${API_URL}/v1/app-assets/upload?deploymentKey=discover-flaeming`,
                              formData,
                              { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
                            );
                            if (response.data.success && response.data.url) {
                              const newScreens = [...onboarding.screens];
                              newScreens[screenIndex] = {
                                ...newScreens[screenIndex],
                                backgroundImage: {
                                  ...newScreens[screenIndex].backgroundImage,
                                  dark: response.data.url
                                }
                              };
                              setOnboarding({...onboarding, screens: newScreens});
                            }
                          } catch (error) {
                            const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
                            if (error.response?.status === 413) {
                              alert('Upload failed: File is too large. Maximum size is 10MB.');
                            } else {
                              alert('Upload failed: ' + errorMsg);
                            }
                          } finally {
                            setUploading(false);
                          }
                        }}
                        className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-700 file:text-gray-200 hover:file:bg-gray-600"
                      />
                    </div>
                  </div>

                  {/* Right: Text Content (Translatable) */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-700">Text Content</h4>
                      <span className="text-xs text-primary-600 bg-primary-50 px-2 py-1 rounded">Translatable</span>
                    </div>

                    {/* Language Tabs for this screen's text */}
                    <LanguageTabs
                      languages={availableLanguages}
                      currentLang={currentLanguage}
                      onChangeLang={setCurrentLanguage}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Headline <span className="text-xs font-normal text-gray-400">(onboarding_{screenIndex + 1}_headline)</span>
                      </label>
                      <input
                        type="text"
                        value={getTranslation(`onboarding_${screenIndex + 1}_headline`)}
                        onChange={(e) => setTranslation(`onboarding_${screenIndex + 1}_headline`, e.target.value)}
                        placeholder={`Enter headline for screen ${screenIndex + 1} (${currentLanguage.toUpperCase()})`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description Text <span className="text-xs font-normal text-gray-400">(onboarding_{screenIndex + 1}_text)</span>
                      </label>
                      <textarea
                        value={getTranslation(`onboarding_${screenIndex + 1}_text`)}
                        onChange={(e) => setTranslation(`onboarding_${screenIndex + 1}_text`, e.target.value)}
                        placeholder={`Enter description for screen ${screenIndex + 1} (${currentLanguage.toUpperCase()})`}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Teasers Tab */}
        {activeTab === 'teasers' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Teasers / Carousel</h2>
                <p className="text-sm text-gray-500 mt-1">Up to 5 promotional cards displayed in the mobile app home screen carousel</p>
              </div>
              {teasers.length < 5 && (
                <button
                  onClick={() => setTeasers(prev => [...prev, {
                    order: prev.length,
                    type: 'featured_challenge',
                    isActive: true,
                    title: '',
                    subtitle: '',
                    description: '',
                    imageUrl: '',
                    ctaText: 'Explore',
                    ctaAction: '',
                    challengeId: null,
                    eventId: null,
                    backgroundColor: '#D97706',
                    textColor: '#FFFFFF',
                    backgroundImageUrl: '',
                    startDate: null,
                    endDate: null,
                    translations: { en: { title: '', subtitle: '', description: '', cta_text: 'Explore' } }
                  }])}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <FiPlus className="w-4 h-4" />
                  Add Teaser
                </button>
              )}
            </div>

            {teasers.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <FiStar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No teasers configured</p>
                <p className="text-gray-400 text-sm mt-1">Add promotional cards to the mobile app carousel</p>
              </div>
            ) : (
              <div className="space-y-4">
                {teasers.map((teaser, index) => (
                  <div key={index} className={`border rounded-lg p-4 ${teaser.isActive ? 'border-primary-300 bg-white' : 'border-gray-200 bg-gray-50 opacity-75'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-gray-500 bg-gray-100 rounded-full w-7 h-7 flex items-center justify-center">{index + 1}</span>
                        <h3 className="font-medium text-gray-900">{teaser.title || `Teaser ${index + 1}`}</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Move Up */}
                        {index > 0 && (
                          <button
                            onClick={() => {
                              const newTeasers = [...teasers];
                              [newTeasers[index - 1], newTeasers[index]] = [newTeasers[index], newTeasers[index - 1]];
                              newTeasers.forEach((t, i) => t.order = i);
                              setTeasers(newTeasers);
                            }}
                            className="text-gray-400 hover:text-gray-600 p-1"
                            title="Move up"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                          </button>
                        )}
                        {/* Move Down */}
                        {index < teasers.length - 1 && (
                          <button
                            onClick={() => {
                              const newTeasers = [...teasers];
                              [newTeasers[index], newTeasers[index + 1]] = [newTeasers[index + 1], newTeasers[index]];
                              newTeasers.forEach((t, i) => t.order = i);
                              setTeasers(newTeasers);
                            }}
                            className="text-gray-400 hover:text-gray-600 p-1"
                            title="Move down"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                          </button>
                        )}
                        {/* Active Toggle */}
                        <button
                          onClick={() => {
                            const newTeasers = [...teasers];
                            newTeasers[index] = { ...newTeasers[index], isActive: !newTeasers[index].isActive };
                            setTeasers(newTeasers);
                          }}
                          className={`px-2 py-1 rounded text-xs font-medium ${teaser.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                        >
                          {teaser.isActive ? 'Active' : 'Inactive'}
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete teaser "${teaser.title || 'Untitled'}"? This action cannot be undone.`)) {
                              const newTeasers = teasers.filter((_, i) => i !== index);
                              newTeasers.forEach((t, i) => t.order = i);
                              setTeasers(newTeasers);
                            }
                          }}
                          className="text-red-400 hover:text-red-600 p-1"
                          title="Delete teaser"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Type */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                        <select
                          value={teaser.type}
                          onChange={(e) => {
                            const newTeasers = [...teasers];
                            newTeasers[index] = {
                              ...newTeasers[index],
                              type: e.target.value,
                              // Clear linked IDs when changing type
                              challengeId: e.target.value === 'featured_challenge' ? newTeasers[index].challengeId : null,
                              eventId: e.target.value === 'event' ? newTeasers[index].eventId : null
                            };
                            setTeasers(newTeasers);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="featured_challenge">Featured Challenge</option>
                          <option value="event">Event</option>
                          <option value="reward">Reward</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>

                      {/* Challenge Selector - shown when type is featured_challenge */}
                      {teaser.type === 'featured_challenge' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Select Challenge</label>
                          <select
                            value={teaser.challengeId || ''}
                            onChange={(e) => {
                              const newTeasers = [...teasers];
                              const selectedChallenge = allChallenges.find(c => c.id === e.target.value);
                              newTeasers[index] = {
                                ...newTeasers[index],
                                challengeId: e.target.value || null,
                                ctaAction: e.target.value ? `challenge:${e.target.value}` : '',
                                // Auto-fill title/description if empty
                                title: newTeasers[index].title || (selectedChallenge?.name || ''),
                                description: newTeasers[index].description || (selectedChallenge?.description?.substring(0, 100) || '')
                              };
                              setTeasers(newTeasers);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="">-- Select a challenge --</option>
                            {allChallenges.map(challenge => (
                              <option key={challenge.id} value={challenge.id}>
                                {challenge.name} ({challenge.type})
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Event Selector - shown when type is event */}
                      {teaser.type === 'event' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Select Event</label>
                          <select
                            value={teaser.eventId || ''}
                            onChange={(e) => {
                              const newTeasers = [...teasers];
                              const selectedEvent = allEvents.find(ev => ev.id === e.target.value);
                              newTeasers[index] = {
                                ...newTeasers[index],
                                eventId: e.target.value || null,
                                ctaAction: e.target.value ? `event:${e.target.value}` : '',
                                // Auto-fill title/description if empty
                                title: newTeasers[index].title || (selectedEvent?.title || ''),
                                description: newTeasers[index].description || (selectedEvent?.description?.substring(0, 100) || '')
                              };
                              setTeasers(newTeasers);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="">-- Select an event --</option>
                            {allEvents.map(event => (
                              <option key={event.id} value={event.id}>
                                {event.title} {event.start_date ? `(${new Date(event.start_date).toLocaleDateString()})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Title */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                        <input
                          type="text"
                          value={teaser.title}
                          onChange={(e) => {
                            const newTeasers = [...teasers];
                            newTeasers[index] = { ...newTeasers[index], title: e.target.value };
                            setTeasers(newTeasers);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="Teaser title"
                        />
                      </div>

                      {/* Subtitle */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Subtitle</label>
                        <input
                          type="text"
                          value={teaser.subtitle}
                          onChange={(e) => {
                            const newTeasers = [...teasers];
                            newTeasers[index] = { ...newTeasers[index], subtitle: e.target.value };
                            setTeasers(newTeasers);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="Teaser subtitle"
                        />
                      </div>

                      {/* CTA Text */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">CTA Button Text</label>
                        <input
                          type="text"
                          value={teaser.ctaText}
                          onChange={(e) => {
                            const newTeasers = [...teasers];
                            newTeasers[index] = { ...newTeasers[index], ctaText: e.target.value };
                            setTeasers(newTeasers);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="Explore"
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                      <textarea
                        value={teaser.description}
                        onChange={(e) => {
                          const newTeasers = [...teasers];
                          newTeasers[index] = { ...newTeasers[index], description: e.target.value };
                          setTeasers(newTeasers);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        rows={2}
                        placeholder="Brief description for the teaser card"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                      {/* Image URL */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Image URL</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={teaser.imageUrl}
                            onChange={(e) => {
                              const newTeasers = [...teasers];
                              newTeasers[index] = { ...newTeasers[index], imageUrl: e.target.value };
                              setTeasers(newTeasers);
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="https://..."
                          />
                          <button
                            type="button"
                            onClick={() => setShowMainImagePicker({ open: true, teaserIndex: index })}
                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-600"
                            title="Pick from gallery"
                          >
                            <FiImage className="w-4 h-4" />
                          </button>
                        </div>
                        {teaser.imageUrl && (
                          <>
                            <img src={teaser.imageUrl} alt="Preview" className="h-16 w-auto mt-2 rounded" onError={(e) => e.target.style.display = 'none'} />
                            {/* Focal Point for main image (if no background image is set) */}
                            {!teaser.backgroundImageUrl && (
                              <div className="mt-2 flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-gray-500">X:</span>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.1"
                                  value={teaser.focalPointX ?? 0.5}
                                  onChange={(e) => {
                                    const newTeasers = [...teasers];
                                    newTeasers[index] = { ...newTeasers[index], focalPointX: parseFloat(e.target.value) };
                                    setTeasers(newTeasers);
                                  }}
                                  className="w-20 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
                                />
                                <span className="text-xs text-gray-500">{((teaser.focalPointX ?? 0.5) * 100).toFixed(0)}%</span>
                                <span className="text-xs text-gray-500 ml-2">Y:</span>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.1"
                                  value={teaser.focalPointY ?? 0.5}
                                  onChange={(e) => {
                                    const newTeasers = [...teasers];
                                    newTeasers[index] = { ...newTeasers[index], focalPointY: parseFloat(e.target.value) };
                                    setTeasers(newTeasers);
                                  }}
                                  className="w-20 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
                                />
                                <span className="text-xs text-gray-500">{((teaser.focalPointY ?? 0.5) * 100).toFixed(0)}%</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* CTA Action */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">CTA Action</label>
                        <input
                          type="text"
                          value={teaser.ctaAction}
                          onChange={(e) => {
                            const newTeasers = [...teasers];
                            newTeasers[index] = { ...newTeasers[index], ctaAction: e.target.value };
                            setTeasers(newTeasers);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="challenge:uuid, event:uuid, or url:https://..."
                        />
                        <p className="text-xs text-gray-400 mt-1">Format: challenge:ID, event:ID, or url:https://...</p>
                      </div>

                      {/* Background Color */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Background Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={teaser.backgroundColor || '#D97706'}
                            onChange={(e) => {
                              const newTeasers = [...teasers];
                              newTeasers[index] = { ...newTeasers[index], backgroundColor: e.target.value };
                              setTeasers(newTeasers);
                            }}
                            className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                          />
                          <input
                            type="text"
                            value={teaser.backgroundColor || ''}
                            onChange={(e) => {
                              const newTeasers = [...teasers];
                              newTeasers[index] = { ...newTeasers[index], backgroundColor: e.target.value };
                              setTeasers(newTeasers);
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="#D97706"
                          />
                        </div>
                      </div>

                      {/* Background Color Transparency */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Background Transparency: {teaser.backgroundColorTransparency ?? 100}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={teaser.backgroundColorTransparency ?? 100}
                          onChange={(e) => {
                            const newTeasers = [...teasers];
                            newTeasers[index] = { ...newTeasers[index], backgroundColorTransparency: parseInt(e.target.value) };
                            setTeasers(newTeasers);
                          }}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                          <span>Transparent</span>
                          <span>Opaque</span>
                        </div>
                      </div>

                      {/* Text Color */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Text Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={teaser.textColor || '#FFFFFF'}
                            onChange={(e) => {
                              const newTeasers = [...teasers];
                              newTeasers[index] = { ...newTeasers[index], textColor: e.target.value };
                              setTeasers(newTeasers);
                            }}
                            className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                          />
                          <input
                            type="text"
                            value={teaser.textColor || ''}
                            onChange={(e) => {
                              const newTeasers = [...teasers];
                              newTeasers[index] = { ...newTeasers[index], textColor: e.target.value };
                              setTeasers(newTeasers);
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="#FFFFFF"
                          />
                        </div>
                      </div>

                      {/* Background Image */}
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Background Image <span className="text-gray-400">(optional, overrides background color)</span>
                        </label>
                        <div className="flex gap-2 mb-2">
                          <button
                            type="button"
                            onClick={() => setShowBgImagePicker({ open: true, teaserIndex: index })}
                            className="flex items-center gap-2 px-3 py-2 bg-primary-50 text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-100 text-sm"
                          >
                            <FiImage className="w-4 h-4" />
                            Select from Gallery
                          </button>
                          <label className="flex items-center gap-2 px-3 py-2 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 cursor-pointer text-sm">
                            <FiUpload className="w-4 h-4" />
                            Upload
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              ref={el => bgImageInputRefs.current[index] = el}
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                try {
                                  setUploading(true);
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  formData.append('deploymentKey', 'discover-flaeming');
                                  formData.append('type', 'teaser_background');
                                  const token = localStorage.getItem('cms_accessToken');
                                  const response = await axios.post(
                                    `${API_URL}/v1/app-assets/upload?deploymentKey=discover-flaeming`,
                                    formData,
                                    { headers: { 'Authorization': `Bearer ${token}` } }
                                  );
                                  if (response.data.success && response.data.url) {
                                    const newTeasers = [...teasers];
                                    newTeasers[index] = { ...newTeasers[index], backgroundImageUrl: response.data.url };
                                    setTeasers(newTeasers);
                                  }
                                } catch (error) {
                                  console.error('Error uploading image:', error);
                                  const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
                                  alert(`Failed to upload image: ${errorMsg}\n\nAllowed: PNG, JPG, WebP (max 5MB)`);
                                } finally {
                                  setUploading(false);
                                  e.target.value = '';
                                }
                              }}
                            />
                          </label>
                          {teaser.backgroundImageUrl && (
                            <button
                              type="button"
                              onClick={() => {
                                const newTeasers = [...teasers];
                                newTeasers[index] = { ...newTeasers[index], backgroundImageUrl: '' };
                                setTeasers(newTeasers);
                              }}
                              className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 text-sm"
                            >
                              <FiX className="w-4 h-4" />
                              Remove
                            </button>
                          )}
                        </div>
                        {/* Focal Point Picker - shows when there's a background image */}
                        {teaser.backgroundImageUrl && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <label className="block text-xs font-medium text-gray-600 mb-2">
                              Image Focal Point <span className="text-gray-400">(select the important part of the image)</span>
                            </label>
                            <div className="flex gap-4">
                              {/* Left: Full image with clickable focal point */}
                              <div className="flex-shrink-0">
                                <p className="text-xs text-gray-500 mb-1">Click to set focus:</p>
                                <div
                                  className="relative w-40 bg-gray-200 rounded overflow-hidden cursor-crosshair border-2 border-gray-300 hover:border-primary-400"
                                  style={{ aspectRatio: '3/4' }}
                                  onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const x = (e.clientX - rect.left) / rect.width;
                                    const y = (e.clientY - rect.top) / rect.height;
                                    const newTeasers = [...teasers];
                                    newTeasers[index] = {
                                      ...newTeasers[index],
                                      focalPointX: Math.max(0, Math.min(1, x)).toFixed(2),
                                      focalPointY: Math.max(0, Math.min(1, y)).toFixed(2)
                                    };
                                    setTeasers(newTeasers);
                                  }}
                                >
                                  <img
                                    src={teaser.backgroundImageUrl.startsWith('http') ? teaser.backgroundImageUrl : `${API_URL.replace('/api', '')}${teaser.backgroundImageUrl}`}
                                    alt="Full image"
                                    className="w-full h-full object-contain"
                                  />
                                  {/* Focal point crosshair indicator */}
                                  <div
                                    className="absolute left-0 right-0 h-0.5 bg-red-500"
                                    style={{ top: `${(teaser.focalPointY ?? 0.5) * 100}%` }}
                                  />
                                  <div
                                    className="absolute top-0 bottom-0 w-0.5 bg-red-500"
                                    style={{ left: `${(teaser.focalPointX ?? 0.5) * 100}%` }}
                                  />
                                  <div
                                    className="absolute w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-md -translate-x-1/2 -translate-y-1/2"
                                    style={{
                                      left: `${(teaser.focalPointX ?? 0.5) * 100}%`,
                                      top: `${(teaser.focalPointY ?? 0.5) * 100}%`
                                    }}
                                  />
                                </div>
                              </div>
                              {/* Right: Result preview + sliders */}
                              <div className="flex-1 space-y-3">
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Mobile preview (2.7:1 crop):</p>
                                  <div
                                    className="relative w-full bg-gray-200 rounded-lg overflow-hidden border border-gray-300"
                                    style={{ aspectRatio: '2.7/1' }}
                                  >
                                    <img
                                      src={teaser.backgroundImageUrl.startsWith('http') ? teaser.backgroundImageUrl : `${API_URL.replace('/api', '')}${teaser.backgroundImageUrl}`}
                                      alt="Crop preview"
                                      className="w-full h-full object-cover"
                                      style={{ objectPosition: `${(teaser.focalPointX ?? 0.5) * 100}% ${(teaser.focalPointY ?? 0.5) * 100}%` }}
                                    />
                                    {/* Text overlay with gradient */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
                                    <div className="absolute bottom-0 left-0 right-0 p-3 z-10" style={{ color: teaser.textColor || '#FFFFFF' }}>
                                      {teaser.title && <p className="font-bold text-sm drop-shadow-md">{teaser.title}</p>}
                                      {teaser.subtitle && <p className="text-xs opacity-95 drop-shadow-sm mt-0.5">{teaser.subtitle}</p>}
                                      {teaser.ctaText && (
                                        <span className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium drop-shadow-sm" style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}>
                                          {teaser.ctaText}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {/* Horizontal slider */}
                                <div>
                                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                                    <span>Left</span>
                                    <span>Horizontal: {((teaser.focalPointX ?? 0.5) * 100).toFixed(0)}%</span>
                                    <span>Right</span>
                                  </div>
                                  <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.05"
                                    value={teaser.focalPointX ?? 0.5}
                                    onChange={(e) => {
                                      const newTeasers = [...teasers];
                                      newTeasers[index] = { ...newTeasers[index], focalPointX: parseFloat(e.target.value) };
                                      setTeasers(newTeasers);
                                    }}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
                                  />
                                </div>
                                {/* Vertical slider */}
                                <div>
                                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                                    <span>Top</span>
                                    <span>Vertical: {((teaser.focalPointY ?? 0.5) * 100).toFixed(0)}%</span>
                                    <span>Bottom</span>
                                  </div>
                                  <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.05"
                                    value={teaser.focalPointY ?? 0.5}
                                    onChange={(e) => {
                                      const newTeasers = [...teasers];
                                      newTeasers[index] = { ...newTeasers[index], focalPointY: parseFloat(e.target.value) };
                                      setTeasers(newTeasers);
                                    }}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Start Date */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Start Date <span className="text-gray-400">(optional)</span></label>
                        <input
                          type="datetime-local"
                          value={teaser.startDate ? new Date(teaser.startDate).toISOString().slice(0, 16) : ''}
                          onChange={(e) => {
                            const newTeasers = [...teasers];
                            newTeasers[index] = { ...newTeasers[index], startDate: e.target.value ? new Date(e.target.value).toISOString() : null };
                            setTeasers(newTeasers);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>

                      {/* End Date */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">End Date <span className="text-gray-400">(optional)</span></label>
                        <input
                          type="datetime-local"
                          value={teaser.endDate ? new Date(teaser.endDate).toISOString().slice(0, 16) : ''}
                          onChange={(e) => {
                            const newTeasers = [...teasers];
                            newTeasers[index] = { ...newTeasers[index], endDate: e.target.value ? new Date(e.target.value).toISOString() : null };
                            setTeasers(newTeasers);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>

                      {/* Preview for teasers without background image */}
                      {!teaser.backgroundImageUrl && (teaser.title || teaser.subtitle) && (
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Preview</label>
                          <div
                            className="p-3 rounded-lg"
                            style={{
                              backgroundColor: teaser.backgroundColor || '#D97706',
                              color: teaser.textColor || '#FFFFFF',
                              aspectRatio: '2.7/1'
                            }}
                          >
                            <div className="h-full flex flex-col justify-end">
                              {teaser.title && <p className="font-bold text-sm truncate">{teaser.title}</p>}
                              {teaser.subtitle && <p className="text-xs opacity-90 truncate">{teaser.subtitle}</p>}
                              {teaser.ctaText && (
                                <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium w-fit" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}>
                                  {teaser.ctaText}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Translations */}
                    <div className="mt-4 border-t border-gray-200 pt-3">
                      <button
                        type="button"
                        onClick={() => setExpandedTeaserTranslations(prev => ({
                          ...prev,
                          [index]: !prev[index]
                        }))}
                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                      >
                        <FiGlobe className="w-4 h-4" />
                        Translations
                        {Object.keys(teaser.translations || {}).filter(l => l !== 'en' && (teaser.translations[l]?.title || teaser.translations[l]?.subtitle)).length > 0 && (
                          <span className="text-xs text-gray-400">
                            ({Object.keys(teaser.translations || {}).filter(l => l !== 'en' && (teaser.translations[l]?.title || teaser.translations[l]?.subtitle)).length} languages)
                          </span>
                        )}
                        <svg className={`w-4 h-4 transition-transform ${expandedTeaserTranslations[index] ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {expandedTeaserTranslations[index] && (
                        <div className="mt-3">
                          <LanguageTabsEditor
                            languages={availableLanguages.filter(l => l.enabled !== false)}
                            defaultLang="en"
                            translations={teaser.translations || {
                              en: { title: teaser.title || '', subtitle: teaser.subtitle || '', description: teaser.description || '', cta_text: teaser.ctaText || '' }
                            }}
                            fields={[
                              { key: 'title', label: 'Title', type: 'input', placeholder: 'Teaser title' },
                              { key: 'subtitle', label: 'Subtitle', type: 'input', placeholder: 'Teaser subtitle' },
                              { key: 'description', label: 'Description', type: 'textarea', rows: 2, placeholder: 'Brief description' },
                              { key: 'cta_text', label: 'CTA Button Text', type: 'input', placeholder: 'Explore' }
                            ]}
                            onChange={(locale, field, value) => {
                              const newTeasers = [...teasers];
                              if (!newTeasers[index].translations) {
                                newTeasers[index].translations = {};
                              }
                              if (!newTeasers[index].translations[locale]) {
                                newTeasers[index].translations[locale] = {};
                              }
                              newTeasers[index].translations[locale][field] = value;
                              // Also update main fields for English
                              if (locale === 'en') {
                                const fieldMap = { title: 'title', subtitle: 'subtitle', description: 'description', cta_text: 'ctaText' };
                                if (fieldMap[field]) {
                                  newTeasers[index][fieldMap[field]] = value;
                                }
                              }
                              setTeasers(newTeasers);
                            }}
                            compact
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

{/* Colors Tab */}
        {activeTab === 'colors' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Color System</h2>
              <div className="flex gap-2 border border-gray-300 rounded-lg p-1">
                <button
                  onClick={() => setColorThemeTab('light')}
                  className={`px-4 py-2 rounded transition-colors ${
                    colorThemeTab === 'light'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <FiSun className="w-4 h-4 text-primary-600" /> Light Mode
                </button>
                <button
                  onClick={() => setColorThemeTab('dark')}
                  className={`px-4 py-2 rounded transition-colors ${
                    colorThemeTab === 'dark'
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <FiMoon className="w-4 h-4" /> Dark Mode
                </button>
              </div>
            </div>

            {colorThemeTab === 'light' ? (
              <div className="space-y-8">
                {/* Palette Colors */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><FiDroplet className="w-5 h-5 text-primary-600" /> Palette Colors</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Primary */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-700 mb-2">{t('cms.appSettings.primaryColor')}</h4>
                      {renderColorInput('Light (400)', lightColors.palette?.primary?.light, (v) =>
                        setLightColors({...lightColors, palette: {...lightColors.palette, primary: {...lightColors.palette?.primary, light: v}}}),
                        '', 'palette_primary_light'
                      )}
                      {renderColorInput('Main (500)', lightColors.palette?.primary?.main, (v) =>
                        setLightColors({...lightColors, palette: {...lightColors.palette, primary: {...lightColors.palette?.primary, main: v}}}),
                        '', 'palette_primary_main'
                      )}
                      {renderColorInput('Dark (600)', lightColors.palette?.primary?.dark, (v) =>
                        setLightColors({...lightColors, palette: {...lightColors.palette, primary: {...lightColors.palette?.primary, dark: v}}}),
                        '', 'palette_primary_dark'
                      )}
                    </div>

                    {/* Secondary */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-700 mb-2">{t('cms.appSettings.secondaryColor')}</h4>
                      {renderColorInput('Light (400)', lightColors.palette?.secondary?.light, (v) =>
                        setLightColors({...lightColors, palette: {...lightColors.palette, secondary: {...lightColors.palette?.secondary, light: v}}}),
                        '', 'palette_secondary_light'
                      )}
                      {renderColorInput('Main (500)', lightColors.palette?.secondary?.main, (v) =>
                        setLightColors({...lightColors, palette: {...lightColors.palette, secondary: {...lightColors.palette?.secondary, main: v}}}),
                        '', 'palette_secondary_main'
                      )}
                      {renderColorInput('Dark (600)', lightColors.palette?.secondary?.dark, (v) =>
                        setLightColors({...lightColors, palette: {...lightColors.palette, secondary: {...lightColors.palette?.secondary, dark: v}}}),
                        '', 'palette_secondary_dark'
                      )}
                    </div>

                    {/* Accent */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-700 mb-2">Accent</h4>
                      {renderColorInput('Light (400)', lightColors.palette?.accent?.light, (v) =>
                        setLightColors({...lightColors, palette: {...lightColors.palette, accent: {...lightColors.palette?.accent, light: v}}}),
                        '', 'palette_accent_light'
                      )}
                      {renderColorInput('Main (500)', lightColors.palette?.accent?.main, (v) =>
                        setLightColors({...lightColors, palette: {...lightColors.palette, accent: {...lightColors.palette?.accent, main: v}}}),
                        '', 'palette_accent_main'
                      )}
                      {renderColorInput('Dark (600)', lightColors.palette?.accent?.dark, (v) =>
                        setLightColors({...lightColors, palette: {...lightColors.palette, accent: {...lightColors.palette?.accent, dark: v}}}),
                        '', 'palette_accent_dark'
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Colors */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><FiZap className="w-5 h-5 text-primary-600" /> Status Colors</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {renderColorInput('Success', lightColors.status?.success, (v) =>
                      setLightColors({...lightColors, status: {...lightColors.status, success: v}}),
                      '', 'status_success'
                    )}
                    {renderColorInput('Warning', lightColors.status?.warning, (v) =>
                      setLightColors({...lightColors, status: {...lightColors.status, warning: v}}),
                      '', 'status_warning'
                    )}
                    {renderColorInput('Error', lightColors.status?.error, (v) =>
                      setLightColors({...lightColors, status: {...lightColors.status, error: v}}),
                      '', 'status_error'
                    )}
                    {renderColorInput('Info', lightColors.status?.info, (v) =>
                      setLightColors({...lightColors, status: {...lightColors.status, info: v}}),
                      '', 'status_info'
                    )}
                  </div>
                </div>

                {/* Button Colors */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><FiCircle className="w-5 h-5 text-primary-600" /> Button Colors</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {renderColorInput('Primary', lightColors.button?.primary, (v) =>
                      setLightColors({...lightColors, button: {...lightColors.button, primary: v}}),
                      '', 'button_primary'
                    )}
                    {renderColorInput('Secondary', lightColors.button?.secondary, (v) =>
                      setLightColors({...lightColors, button: {...lightColors.button, secondary: v}}),
                      '', 'button_secondary'
                    )}
                    {renderColorInput('Disabled', lightColors.button?.disabled, (v) =>
                      setLightColors({...lightColors, button: {...lightColors.button, disabled: v}}),
                      '', 'button_disabled'
                    )}
                  </div>
                </div>


                {/* Background Colors */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><FiLayout className="w-5 h-5 text-primary-600" /> Background Colors</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderColorInput('App Background', lightColors.background?.app, (v) =>
                      setLightColors({...lightColors, background: {...lightColors.background, app: v}}),
                      'Main app background color (used everywhere by default)', 'background_app'
                    )}
                  </div>
                </div>


                {/* Text Colors */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><FiEdit3 className="w-5 h-5 text-primary-600" /> Text Colors</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {renderColorInput('Primary Text', lightColors.text?.primary, (v) =>
                      setLightColors({...lightColors, text: {...lightColors.text, primary: v}}),
                      'Main body text color', 'text_primary'
                    )}
                    {renderColorInput('Secondary Text', lightColors.text?.secondary, (v) =>
                      setLightColors({...lightColors, text: {...lightColors.text, secondary: v}}),
                      'Subtitles, captions, less important text', 'text_secondary'
                    )}
                    {renderColorInput('Link', lightColors.text?.link, (v) =>
                      setLightColors({...lightColors, text: {...lightColors.text, link: v}}),
                      'Hyperlinks and interactive text', 'text_link'
                    )}
                    {renderColorInput('Neutral Dark', lightColors.text?.neutralDark, (v) =>
                      setLightColors({...lightColors, text: {...lightColors.text, neutralDark: v}}),
                      'Neutral dark text for emphasis', 'text_neutralDark'
                    )}
                    {renderColorInput('Neutral Light', lightColors.text?.neutralLight, (v) =>
                      setLightColors({...lightColors, text: {...lightColors.text, neutralLight: v}}),
                      'Neutral light text for subtle content', 'text_neutralLight'
                    )}
                  </div>
                </div>

                {/* Input Colors - Primary Style */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><FiClipboard className="w-5 h-5 text-primary-600" /> Input Colors - Primary Style</h3>
                  <p className="text-sm text-gray-600 mb-3">Default input field style (used over solid backgrounds)</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {renderColorInput('Background', lightColors.input?.primary?.background, (v) =>
                      setLightColors({...lightColors, input: {...lightColors.input, primary: {...lightColors.input?.primary, background: v}}}),
                      '', 'input_primary_background'
                    )}
                    {renderColorInput('Text', lightColors.input?.primary?.text, (v) =>
                      setLightColors({...lightColors, input: {...lightColors.input, primary: {...lightColors.input?.primary, text: v}}}),
                      '', 'input_primary_text'
                    )}
                    {renderColorInput('Hint', lightColors.input?.primary?.hint, (v) =>
                      setLightColors({...lightColors, input: {...lightColors.input, primary: {...lightColors.input?.primary, hint: v}}}),
                      '', 'input_primary_hint'
                    )}
                    {renderColorInput('Border', lightColors.input?.primary?.border, (v) =>
                      setLightColors({...lightColors, input: {...lightColors.input, primary: {...lightColors.input?.primary, border: v}}}),
                      '', 'input_primary_border'
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Background Transparency <span className="text-xs font-normal text-gray-400">(input_primary_background_transparency)</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={lightColors.input?.primary?.backgroundTransparency ?? 100}
                          onChange={(e) => setLightColors({...lightColors, input: {...lightColors.input, primary: {...lightColors.input?.primary, backgroundTransparency: parseInt(e.target.value)}}})}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-700 min-w-[45px]">{lightColors.input?.primary?.backgroundTransparency ?? 100}%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Border Width <span className="text-xs font-normal text-gray-400">(input_primary_border_width)</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max="10"
                          value={lightColors.input?.primary?.borderWidth ?? 2}
                          onChange={(e) => setLightColors({...lightColors, input: {...lightColors.input, primary: {...lightColors.input?.primary, borderWidth: parseInt(e.target.value)}}})}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-700 min-w-[45px]">{lightColors.input?.primary?.borderWidth ?? 2}px</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Input Colors - Secondary Style */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">📋 Input Colors - Secondary Style</h3>
                  <p className="text-sm text-gray-600 mb-3">Alternative input style (used over photos/varied backgrounds)</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {renderColorInput('Background', lightColors.input?.secondary?.background, (v) =>
                      setLightColors({...lightColors, input: {...lightColors.input, secondary: {...lightColors.input?.secondary, background: v}}}),
                      '', 'input_secondary_background'
                    )}
                    {renderColorInput('Text', lightColors.input?.secondary?.text, (v) =>
                      setLightColors({...lightColors, input: {...lightColors.input, secondary: {...lightColors.input?.secondary, text: v}}}),
                      '', 'input_secondary_text'
                    )}
                    {renderColorInput('Hint', lightColors.input?.secondary?.hint, (v) =>
                      setLightColors({...lightColors, input: {...lightColors.input, secondary: {...lightColors.input?.secondary, hint: v}}}),
                      '', 'input_secondary_hint'
                    )}
                    {renderColorInput('Border', lightColors.input?.secondary?.border, (v) =>
                      setLightColors({...lightColors, input: {...lightColors.input, secondary: {...lightColors.input?.secondary, border: v}}}),
                      '', 'input_secondary_border'
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Background Transparency <span className="text-xs font-normal text-gray-400">(input_secondary_background_transparency)</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={lightColors.input?.secondary?.backgroundTransparency ?? 100}
                          onChange={(e) => setLightColors({...lightColors, input: {...lightColors.input, secondary: {...lightColors.input?.secondary, backgroundTransparency: parseInt(e.target.value)}}})}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-700 min-w-[45px]">{lightColors.input?.secondary?.backgroundTransparency ?? 100}%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Border Width <span className="text-xs font-normal text-gray-400">(input_secondary_border_width)</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max="10"
                          value={lightColors.input?.secondary?.borderWidth ?? 2}
                          onChange={(e) => setLightColors({...lightColors, input: {...lightColors.input, secondary: {...lightColors.input?.secondary, borderWidth: parseInt(e.target.value)}}})}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-700 min-w-[45px]">{lightColors.input?.secondary?.borderWidth ?? 2}px</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider Color */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">➖ Divider</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {renderColorInput('Divider Color', lightColors.divider, (v) =>
                      setLightColors({...lightColors, divider: v}),
                      'Separator lines between sections', 'divider'
                    )}
                  </div>
                </div>


                {/* Screen Backgrounds Section */}
                <div className="border-t-2 border-gray-300 pt-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2"><FiSmartphone className="w-5 h-5 text-primary-600" /> Screen Backgrounds (Light Mode)</h3>
                  <p className="text-sm text-gray-600 mb-6">Configure background colors and images for each screen in light mode</p>

                  <div className="space-y-6">
                    {/* Splash Screen */}
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><FiSun className="w-4 h-4 text-primary-600" /> Splash Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.light?.splash?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, splash: {...screenBackgrounds.light?.splash, backgroundColor: v}}}),
                            '', 'screen_bg_color (splash)'
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.light?.splash?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, splash: {...screenBackgrounds.light?.splash, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (splash)'
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.light?.splash?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, splash: {...screenBackgrounds.light?.splash, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-700 min-w-[45px]">{screenBackgrounds.light?.splash?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (splash))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.light?.splash?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, splash: {...screenBackgrounds.light?.splash, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/splash-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-400">(bg_image_transparency)</span>: {screenBackgrounds.light?.splash?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.light?.splash?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, splash: {...screenBackgrounds.light?.splash, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.light?.splash?.backgroundImage && (
                        <img src={screenBackgrounds.light.splash.backgroundImage} alt="Splash Preview" className="mt-3 w-full h-32 object-cover rounded border" style={{opacity: (screenBackgrounds.light.splash.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>

                    {/* Login Screen */}
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><FiLock className="w-4 h-4 text-primary-600" /> Login Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.light?.login?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, login: {...screenBackgrounds.light?.login, backgroundColor: v}}}),
                            '', 'screen_bg_color (login)'
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.light?.login?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, login: {...screenBackgrounds.light?.login, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (login)'
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.light?.login?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, login: {...screenBackgrounds.light?.login, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-700 min-w-[45px]">{screenBackgrounds.light?.login?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (login))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.light?.login?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, login: {...screenBackgrounds.light?.login, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/login-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-400">(bg_image_transparency)</span>: {screenBackgrounds.light?.login?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.light?.login?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, login: {...screenBackgrounds.light?.login, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.light?.login?.backgroundImage && (
                        <img src={screenBackgrounds.light.login.backgroundImage} alt="Login Preview" className="mt-3 w-full h-32 object-cover rounded border" style={{opacity: (screenBackgrounds.light.login.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>

                    {/* Home/Dashboard Screen */}
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><FiHome className="w-4 h-4 text-primary-600" /> Home/Dashboard Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.light?.home?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, home: {...screenBackgrounds.light?.home, backgroundColor: v}}}),
                            '', 'screen_bg_color (home)'
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.light?.home?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, home: {...screenBackgrounds.light?.home, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (home)'
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.light?.home?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, home: {...screenBackgrounds.light?.home, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-700 min-w-[45px]">{screenBackgrounds.light?.home?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (home))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.light?.home?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, home: {...screenBackgrounds.light?.home, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/home-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-400">(bg_image_transparency)</span>: {screenBackgrounds.light?.home?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.light?.home?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, home: {...screenBackgrounds.light?.home, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.light?.home?.backgroundImage && (
                        <img src={screenBackgrounds.light.home.backgroundImage} alt="Home Preview" className="mt-3 w-full h-32 object-cover rounded border" style={{opacity: (screenBackgrounds.light.home.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>

                    {/* Map Screen */}
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><FiMap className="w-4 h-4 text-primary-600" /> Map Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.light?.map?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, map: {...screenBackgrounds.light?.map, backgroundColor: v}}}),
                            '', 'screen_bg_color (map)'
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.light?.map?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, map: {...screenBackgrounds.light?.map, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (map)'
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.light?.map?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, map: {...screenBackgrounds.light?.map, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-700 min-w-[45px]">{screenBackgrounds.light?.map?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (map))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.light?.map?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, map: {...screenBackgrounds.light?.map, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/map-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-400">(bg_image_transparency)</span>: {screenBackgrounds.light?.map?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.light?.map?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, map: {...screenBackgrounds.light?.map, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.light?.map?.backgroundImage && (
                        <img src={screenBackgrounds.light.map.backgroundImage} alt="Map Preview" className="mt-3 w-full h-32 object-cover rounded border" style={{opacity: (screenBackgrounds.light.map.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>

                    {/* Achievements Screen */}
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><FiAward className="w-4 h-4 text-primary-600" /> Achievements Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.light?.achievements?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, achievements: {...screenBackgrounds.light?.achievements, backgroundColor: v}}}),
                            '', 'screen_bg_color (achievements)'
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.light?.achievements?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, achievements: {...screenBackgrounds.light?.achievements, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (achievements)'
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.light?.achievements?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, achievements: {...screenBackgrounds.light?.achievements, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-700 min-w-[45px]">{screenBackgrounds.light?.achievements?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (achievements))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.light?.achievements?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, achievements: {...screenBackgrounds.light?.achievements, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/achievements-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-400">(bg_image_transparency)</span>: {screenBackgrounds.light?.achievements?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.light?.achievements?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, achievements: {...screenBackgrounds.light?.achievements, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.light?.achievements?.backgroundImage && (
                        <img src={screenBackgrounds.light.achievements.backgroundImage} alt="Achievements Preview" className="mt-3 w-full h-32 object-cover rounded border" style={{opacity: (screenBackgrounds.light.achievements.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>

                    {/* Events Screen */}
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><FiCalendar className="w-4 h-4 text-primary-600" /> Events Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.light?.events?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, events: {...screenBackgrounds.light?.events, backgroundColor: v}}}),
                            '', 'screen_bg_color (events)'
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.light?.events?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, events: {...screenBackgrounds.light?.events, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (events)'
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.light?.events?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, events: {...screenBackgrounds.light?.events, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-700 min-w-[45px]">{screenBackgrounds.light?.events?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (events))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.light?.events?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, events: {...screenBackgrounds.light?.events, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/events-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-400">(bg_image_transparency)</span>: {screenBackgrounds.light?.events?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.light?.events?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, events: {...screenBackgrounds.light?.events, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.light?.events?.backgroundImage && (
                        <img src={screenBackgrounds.light.events.backgroundImage} alt="Events Preview" className="mt-3 w-full h-32 object-cover rounded border" style={{opacity: (screenBackgrounds.light.events.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>

                    {/* Rewards Screen */}
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><FiGift className="w-4 h-4 text-primary-600" /> Rewards Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.light?.rewards?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, rewards: {...screenBackgrounds.light?.rewards, backgroundColor: v}}}),
                            '', 'screen_bg_color (rewards)'
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.light?.rewards?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, rewards: {...screenBackgrounds.light?.rewards, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (rewards)'
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.light?.rewards?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, rewards: {...screenBackgrounds.light?.rewards, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-700 min-w-[45px]">{screenBackgrounds.light?.rewards?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (rewards))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.light?.rewards?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, rewards: {...screenBackgrounds.light?.rewards, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/rewards-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-400">(bg_image_transparency)</span>: {screenBackgrounds.light?.rewards?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.light?.rewards?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, light: {...screenBackgrounds.light, rewards: {...screenBackgrounds.light?.rewards, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.light?.rewards?.backgroundImage && (
                        <img src={screenBackgrounds.light.rewards.backgroundImage} alt="Rewards Preview" className="mt-3 w-full h-32 object-cover rounded border" style={{opacity: (screenBackgrounds.light.rewards.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Info box */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <p className="text-sm text-primary-800">
                    <FiSun className="w-4 h-4 inline mr-1 text-primary-600" /> <strong>Light Mode:</strong> Colors optimized for bright backgrounds and daytime use.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-8 bg-gray-900 p-6 rounded-lg">
                {/* Dark Mode - Palette Colors */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2"><FiDroplet className="w-5 h-5 text-primary-400" /> Palette Colors</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Primary */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-300 mb-2">{t('cms.appSettings.primaryColor')}</h4>
                      {renderColorInput('Light (400)', darkColors.palette?.primary?.light, (v) =>
                        setDarkColors({...darkColors, palette: {...darkColors.palette, primary: {...darkColors.palette?.primary, light: v}}}),
                        '', 'palette_primary_light', true
                      )}
                      {renderColorInput('Main (500)', darkColors.palette?.primary?.main, (v) =>
                        setDarkColors({...darkColors, palette: {...darkColors.palette, primary: {...darkColors.palette?.primary, main: v}}}),
                        '', 'palette_primary_main', true
                      )}
                      {renderColorInput('Dark (600)', darkColors.palette?.primary?.dark, (v) =>
                        setDarkColors({...darkColors, palette: {...darkColors.palette, primary: {...darkColors.palette?.primary, dark: v}}}),
                        '', 'palette_primary_dark', true
                      )}
                    </div>

                    {/* Secondary */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-300 mb-2">{t('cms.appSettings.secondaryColor')}</h4>
                      {renderColorInput('Light (400)', darkColors.palette?.secondary?.light, (v) =>
                        setDarkColors({...darkColors, palette: {...darkColors.palette, secondary: {...darkColors.palette?.secondary, light: v}}}),
                        '', 'palette_secondary_light', true
                      )}
                      {renderColorInput('Main (500)', darkColors.palette?.secondary?.main, (v) =>
                        setDarkColors({...darkColors, palette: {...darkColors.palette, secondary: {...darkColors.palette?.secondary, main: v}}}),
                        '', 'palette_secondary_main', true
                      )}
                      {renderColorInput('Dark (600)', darkColors.palette?.secondary?.dark, (v) =>
                        setDarkColors({...darkColors, palette: {...darkColors.palette, secondary: {...darkColors.palette?.secondary, dark: v}}}),
                        '', 'palette_secondary_dark', true
                      )}
                    </div>

                    {/* Accent */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-300 mb-2">Accent</h4>
                      {renderColorInput('Light (400)', darkColors.palette?.accent?.light, (v) =>
                        setDarkColors({...darkColors, palette: {...darkColors.palette, accent: {...darkColors.palette?.accent, light: v}}}),
                        '', 'palette_accent_light', true
                      )}
                      {renderColorInput('Main (500)', darkColors.palette?.accent?.main, (v) =>
                        setDarkColors({...darkColors, palette: {...darkColors.palette, accent: {...darkColors.palette?.accent, main: v}}}),
                        '', 'palette_accent_main', true
                      )}
                      {renderColorInput('Dark (600)', darkColors.palette?.accent?.dark, (v) =>
                        setDarkColors({...darkColors, palette: {...darkColors.palette, accent: {...darkColors.palette?.accent, dark: v}}}),
                        '', 'palette_accent_dark', true
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Colors */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-4">⚡ Status Colors</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {renderColorInput('Success', darkColors.status?.success, (v) =>
                      setDarkColors({...darkColors, status: {...darkColors.status, success: v}}),
                      '', 'status_success', true
                    )}
                    {renderColorInput('Warning', darkColors.status?.warning, (v) =>
                      setDarkColors({...darkColors, status: {...darkColors.status, warning: v}}),
                      '', 'status_warning', true
                    )}
                    {renderColorInput('Error', darkColors.status?.error, (v) =>
                      setDarkColors({...darkColors, status: {...darkColors.status, error: v}}),
                      '', 'status_error', true
                    )}
                    {renderColorInput('Info', darkColors.status?.info, (v) =>
                      setDarkColors({...darkColors, status: {...darkColors.status, info: v}}),
                      '', 'status_info', true
                    )}
                  </div>
                </div>

                {/* Button Colors */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-4">🔘 Button Colors</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {renderColorInput('Primary', darkColors.button?.primary, (v) =>
                      setDarkColors({...darkColors, button: {...darkColors.button, primary: v}}),
                      '', 'button_primary', true
                    )}
                    {renderColorInput('Secondary', darkColors.button?.secondary, (v) =>
                      setDarkColors({...darkColors, button: {...darkColors.button, secondary: v}}),
                      '', 'button_secondary', true
                    )}
                    {renderColorInput('Disabled', darkColors.button?.disabled, (v) =>
                      setDarkColors({...darkColors, button: {...darkColors.button, disabled: v}}),
                      '', 'button_disabled', true
                    )}
                  </div>
                </div>


                {/* Background Colors */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-4">🖼️ Background Colors</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderColorInput('App Background', darkColors.background?.app, (v) =>
                      setDarkColors({...darkColors, background: {...darkColors.background, app: v}}),
                      '', 'background_app', true
                    )}
                  </div>
                </div>


                {/* Text Colors */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2"><FiEdit3 className="w-5 h-5 text-primary-400" /> Text Colors</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {renderColorInput('Primary Text', darkColors.text?.primary, (v) =>
                      setDarkColors({...darkColors, text: {...darkColors.text, primary: v}}),
                      'Main body text color', 'text_primary', true
                    )}
                    {renderColorInput('Secondary Text', darkColors.text?.secondary, (v) =>
                      setDarkColors({...darkColors, text: {...darkColors.text, secondary: v}}),
                      'Subtle secondary text', 'text_secondary', true
                    )}
                    {renderColorInput('Link', darkColors.text?.link, (v) =>
                      setDarkColors({...darkColors, text: {...darkColors.text, link: v}}),
                      'Hyperlink color', 'text_link', true
                    )}
                    {renderColorInput('Neutral Dark', darkColors.text?.neutralDark, (v) =>
                      setDarkColors({...darkColors, text: {...darkColors.text, neutralDark: v}}),
                      'Neutral dark text for emphasis', 'text_neutralDark', true
                    )}
                    {renderColorInput('Neutral Light', darkColors.text?.neutralLight, (v) =>
                      setDarkColors({...darkColors, text: {...darkColors.text, neutralLight: v}}),
                      'Neutral light text for subtle content', 'text_neutralLight', true
                    )}
                  </div>
                </div>

                {/* Input Colors - Primary Style */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-4">📋 Input Colors - Primary Style</h3>
                  <p className="text-sm text-gray-400 mb-3">Default input field style</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {renderColorInput('Background', darkColors.input?.primary?.background, (v) =>
                      setDarkColors({...darkColors, input: {...darkColors.input, primary: {...darkColors.input?.primary, background: v}}}),
                      '', 'input_primary_background', true
                    )}
                    {renderColorInput('Text', darkColors.input?.primary?.text, (v) =>
                      setDarkColors({...darkColors, input: {...darkColors.input, primary: {...darkColors.input?.primary, text: v}}}),
                      '', 'input_primary_text', true
                    )}
                    {renderColorInput('Hint', darkColors.input?.primary?.hint, (v) =>
                      setDarkColors({...darkColors, input: {...darkColors.input, primary: {...darkColors.input?.primary, hint: v}}}),
                      '', 'input_primary_hint', true
                    )}
                    {renderColorInput('Border', darkColors.input?.primary?.border, (v) =>
                      setDarkColors({...darkColors, input: {...darkColors.input, primary: {...darkColors.input?.primary, border: v}}}),
                      '', 'input_primary_border', true
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Background Transparency <span className="text-xs font-normal text-gray-500">(input_primary_background_transparency)</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={darkColors.input?.primary?.backgroundTransparency ?? 100}
                          onChange={(e) => setDarkColors({...darkColors, input: {...darkColors.input, primary: {...darkColors.input?.primary, backgroundTransparency: parseInt(e.target.value)}}})}
                          className="flex-1 h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-300 min-w-[45px]">{darkColors.input?.primary?.backgroundTransparency ?? 100}%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Border Width <span className="text-xs font-normal text-gray-500">(input_primary_border_width)</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max="10"
                          value={darkColors.input?.primary?.borderWidth ?? 2}
                          onChange={(e) => setDarkColors({...darkColors, input: {...darkColors.input, primary: {...darkColors.input?.primary, borderWidth: parseInt(e.target.value)}}})}
                          className="flex-1 h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-300 min-w-[45px]">{darkColors.input?.primary?.borderWidth ?? 2}px</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Input Colors - Secondary Style */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-4">📋 Input Colors - Secondary Style</h3>
                  <p className="text-sm text-gray-400 mb-3">Alternative input style (for photos/varied backgrounds)</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {renderColorInput('Background', darkColors.input?.secondary?.background, (v) =>
                      setDarkColors({...darkColors, input: {...darkColors.input, secondary: {...darkColors.input?.secondary, background: v}}}),
                      '', 'input_secondary_background', true
                    )}
                    {renderColorInput('Text', darkColors.input?.secondary?.text, (v) =>
                      setDarkColors({...darkColors, input: {...darkColors.input, secondary: {...darkColors.input?.secondary, text: v}}}),
                      '', 'input_secondary_text', true
                    )}
                    {renderColorInput('Hint', darkColors.input?.secondary?.hint, (v) =>
                      setDarkColors({...darkColors, input: {...darkColors.input, secondary: {...darkColors.input?.secondary, hint: v}}}),
                      '', 'input_secondary_hint', true
                    )}
                    {renderColorInput('Border', darkColors.input?.secondary?.border, (v) =>
                      setDarkColors({...darkColors, input: {...darkColors.input, secondary: {...darkColors.input?.secondary, border: v}}}),
                      '', 'input_secondary_border', true
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Background Transparency <span className="text-xs font-normal text-gray-500">(input_secondary_background_transparency)</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={darkColors.input?.secondary?.backgroundTransparency ?? 100}
                          onChange={(e) => setDarkColors({...darkColors, input: {...darkColors.input, secondary: {...darkColors.input?.secondary, backgroundTransparency: parseInt(e.target.value)}}})}
                          className="flex-1 h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-300 min-w-[45px]">{darkColors.input?.secondary?.backgroundTransparency ?? 100}%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Border Width <span className="text-xs font-normal text-gray-500">(input_secondary_border_width)</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max="10"
                          value={darkColors.input?.secondary?.borderWidth ?? 2}
                          onChange={(e) => setDarkColors({...darkColors, input: {...darkColors.input, secondary: {...darkColors.input?.secondary, borderWidth: parseInt(e.target.value)}}})}
                          className="flex-1 h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-300 min-w-[45px]">{darkColors.input?.secondary?.borderWidth ?? 2}px</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider Color */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-4">➖ Divider</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {renderColorInput('Divider Color', darkColors.divider, (v) =>
                      setDarkColors({...darkColors, divider: v}),
                      'Separator lines between sections', 'divider', true
                    )}
                  </div>
                </div>


                {/* Screen Backgrounds Section - Dark Mode */}
                <div className="border-t-2 border-gray-700 pt-8">
                  <h3 className="text-xl font-bold text-gray-100 mb-4 flex items-center gap-2"><FiSmartphone className="w-5 h-5 text-primary-400" /> Screen Backgrounds (Dark Mode)</h3>
                  <p className="text-sm text-gray-400 mb-6">Configure background colors and images for each screen in dark mode</p>

                  <div className="space-y-6">
                    {/* Splash Screen Dark */}
                    <div className="border border-gray-700 rounded-lg p-4 bg-gray-800">
                      <h4 className="font-semibold text-gray-200 mb-3">🌅 Splash Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.dark?.splash?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, splash: {...screenBackgrounds.dark?.splash, backgroundColor: v}}}),
                            '', 'screen_bg_color (splash)', true
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.dark?.splash?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, splash: {...screenBackgrounds.dark?.splash, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (splash)', true
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.dark?.splash?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, splash: {...screenBackgrounds.dark?.splash, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-200 min-w-[45px]">{screenBackgrounds.dark?.splash?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (splash))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.dark?.splash?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, splash: {...screenBackgrounds.dark?.splash, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/splash-dark-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-500">(bg_image_transparency)</span>: {screenBackgrounds.dark?.splash?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.dark?.splash?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, splash: {...screenBackgrounds.dark?.splash, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.dark?.splash?.backgroundImage && (
                        <img src={screenBackgrounds.dark.splash.backgroundImage} alt="Splash Preview" className="mt-3 w-full h-32 object-cover rounded border border-gray-600" style={{opacity: (screenBackgrounds.dark.splash.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>

                    {/* Login Screen Dark */}
                    <div className="border border-gray-700 rounded-lg p-4 bg-gray-800">
                      <h4 className="font-semibold text-gray-200 mb-3 flex items-center gap-2"><FiLock className="w-4 h-4 text-primary-400" /> Login Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.dark?.login?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, login: {...screenBackgrounds.dark?.login, backgroundColor: v}}}),
                            '', 'screen_bg_color (login)', true
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.dark?.login?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, login: {...screenBackgrounds.dark?.login, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (login)', true
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.dark?.login?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, login: {...screenBackgrounds.dark?.login, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-200 min-w-[45px]">{screenBackgrounds.dark?.login?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (login))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.dark?.login?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, login: {...screenBackgrounds.dark?.login, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/login-dark-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-500">(bg_image_transparency)</span>: {screenBackgrounds.dark?.login?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.dark?.login?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, login: {...screenBackgrounds.dark?.login, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.dark?.login?.backgroundImage && (
                        <img src={screenBackgrounds.dark.login.backgroundImage} alt="Login Preview" className="mt-3 w-full h-32 object-cover rounded border border-gray-600" style={{opacity: (screenBackgrounds.dark.login.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>

                    {/* Home/Dashboard Screen Dark */}
                    <div className="border border-gray-700 rounded-lg p-4 bg-gray-800">
                      <h4 className="font-semibold text-gray-200 mb-3 flex items-center gap-2"><FiHome className="w-4 h-4 text-primary-400" /> Home/Dashboard Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.dark?.home?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, home: {...screenBackgrounds.dark?.home, backgroundColor: v}}}),
                            '', 'screen_bg_color (home)', true
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.dark?.home?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, home: {...screenBackgrounds.dark?.home, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (home)', true
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.dark?.home?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, home: {...screenBackgrounds.dark?.home, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-200 min-w-[45px]">{screenBackgrounds.dark?.home?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (home))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.dark?.home?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, home: {...screenBackgrounds.dark?.home, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/home-dark-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-500">(bg_image_transparency)</span>: {screenBackgrounds.dark?.home?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.dark?.home?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, home: {...screenBackgrounds.dark?.home, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.dark?.home?.backgroundImage && (
                        <img src={screenBackgrounds.dark.home.backgroundImage} alt="Home Dark Preview" className="mt-3 w-full h-32 object-cover rounded border border-gray-600" style={{opacity: (screenBackgrounds.dark.home.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>

                    {/* Map Screen Dark */}
                    <div className="border border-gray-700 rounded-lg p-4 bg-gray-800">
                      <h4 className="font-semibold text-gray-200 mb-3 flex items-center gap-2"><FiMap className="w-4 h-4 text-primary-400" /> Map Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.dark?.map?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, map: {...screenBackgrounds.dark?.map, backgroundColor: v}}}),
                            '', 'screen_bg_color (map)', true
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.dark?.map?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, map: {...screenBackgrounds.dark?.map, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (map)', true
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.dark?.map?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, map: {...screenBackgrounds.dark?.map, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-200 min-w-[45px]">{screenBackgrounds.dark?.map?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (map))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.dark?.map?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, map: {...screenBackgrounds.dark?.map, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/map-dark-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-500">(bg_image_transparency)</span>: {screenBackgrounds.dark?.map?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.dark?.map?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, map: {...screenBackgrounds.dark?.map, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.dark?.map?.backgroundImage && (
                        <img src={screenBackgrounds.dark.map.backgroundImage} alt="Map Dark Preview" className="mt-3 w-full h-32 object-cover rounded border border-gray-600" style={{opacity: (screenBackgrounds.dark.map.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>

                    {/* Achievements Screen Dark */}
                    <div className="border border-gray-700 rounded-lg p-4 bg-gray-800">
                      <h4 className="font-semibold text-gray-200 mb-3 flex items-center gap-2"><FiAward className="w-4 h-4 text-primary-400" /> Achievements Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.dark?.achievements?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, achievements: {...screenBackgrounds.dark?.achievements, backgroundColor: v}}}),
                            '', 'screen_bg_color (achievements)', true
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.dark?.achievements?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, achievements: {...screenBackgrounds.dark?.achievements, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (achievements)', true
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.dark?.achievements?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, achievements: {...screenBackgrounds.dark?.achievements, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-200 min-w-[45px]">{screenBackgrounds.dark?.achievements?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (achievements))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.dark?.achievements?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, achievements: {...screenBackgrounds.dark?.achievements, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/achievements-dark-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-500">(bg_image_transparency)</span>: {screenBackgrounds.dark?.achievements?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.dark?.achievements?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, achievements: {...screenBackgrounds.dark?.achievements, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.dark?.achievements?.backgroundImage && (
                        <img src={screenBackgrounds.dark.achievements.backgroundImage} alt="Achievements Dark Preview" className="mt-3 w-full h-32 object-cover rounded border border-gray-600" style={{opacity: (screenBackgrounds.dark.achievements.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>

                    {/* Events Screen Dark */}
                    <div className="border border-gray-700 rounded-lg p-4 bg-gray-800">
                      <h4 className="font-semibold text-gray-200 mb-3 flex items-center gap-2"><FiCalendar className="w-4 h-4 text-primary-400" /> Events Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.dark?.events?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, events: {...screenBackgrounds.dark?.events, backgroundColor: v}}}),
                            '', 'screen_bg_color (events)', true
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.dark?.events?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, events: {...screenBackgrounds.dark?.events, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (events)', true
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.dark?.events?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, events: {...screenBackgrounds.dark?.events, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-200 min-w-[45px]">{screenBackgrounds.dark?.events?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (events))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.dark?.events?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, events: {...screenBackgrounds.dark?.events, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/events-dark-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-500">(bg_image_transparency)</span>: {screenBackgrounds.dark?.events?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.dark?.events?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, events: {...screenBackgrounds.dark?.events, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.dark?.events?.backgroundImage && (
                        <img src={screenBackgrounds.dark.events.backgroundImage} alt="Events Dark Preview" className="mt-3 w-full h-32 object-cover rounded border border-gray-600" style={{opacity: (screenBackgrounds.dark.events.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>

                    {/* Rewards Screen Dark */}
                    <div className="border border-gray-700 rounded-lg p-4 bg-gray-800">
                      <h4 className="font-semibold text-gray-200 mb-3 flex items-center gap-2"><FiGift className="w-4 h-4 text-primary-400" /> Rewards Screen</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          {renderColorInput('Background Color', screenBackgrounds.dark?.rewards?.backgroundColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, rewards: {...screenBackgrounds.dark?.rewards, backgroundColor: v}}}),
                            '', 'screen_bg_color (rewards)', true
                          )}
                        </div>
                        <div>
                          {renderColorInput('Surface Color', screenBackgrounds.dark?.rewards?.surfaceColor, (v) =>
                            setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, rewards: {...screenBackgrounds.dark?.rewards, surfaceColor: v}}}),
                            'For cards/panels on this screen', 'surface_color (rewards)', true
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Surface Transparency <span className="text-xs font-normal text-gray-400">(surface_transparency)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={screenBackgrounds.dark?.rewards?.surfaceTransparency || 100}
                              onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, rewards: {...screenBackgrounds.dark?.rewards, surfaceTransparency: parseInt(e.target.value)}}})}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-200 min-w-[45px]">{screenBackgrounds.dark?.rewards?.surfaceTransparency || 100}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Background Image URL <span className="text-xs font-normal text-gray-400">(screen_bg_image (rewards))</span>
                        </label>
                        <input
                          type="text"
                          value={screenBackgrounds.dark?.rewards?.backgroundImage || ''}
                          onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, rewards: {...screenBackgrounds.dark?.rewards, backgroundImage: e.target.value}}})}
                          placeholder="https://example.com/rewards-dark-bg.jpg"
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Image Transparency <span className="text-xs font-normal text-gray-500">(bg_image_transparency)</span>: {screenBackgrounds.dark?.rewards?.backgroundImageTransparency || 100}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={screenBackgrounds.dark?.rewards?.backgroundImageTransparency || 100}
                            onChange={(e) => setScreenBackgrounds({...screenBackgrounds, dark: {...screenBackgrounds.dark, rewards: {...screenBackgrounds.dark?.rewards, backgroundImageTransparency: parseInt(e.target.value)}}})}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {screenBackgrounds.dark?.rewards?.backgroundImage && (
                        <img src={screenBackgrounds.dark.rewards.backgroundImage} alt="Rewards Dark Preview" className="mt-3 w-full h-32 object-cover rounded border border-gray-600" style={{opacity: (screenBackgrounds.dark.rewards.backgroundImageTransparency || 100) / 100}} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Info box */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                  <p className="text-sm text-gray-300">
                    <FiMoon className="w-4 h-4 inline mr-1 text-primary-400" /> <strong className="text-white">Dark Mode:</strong> Colors optimized for dark backgrounds and nighttime use (brighter for visibility).
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Typography Tab */}
        {activeTab === 'typography' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Typography</h2>
            <div className="grid grid-cols-1 gap-4">
              {typography.map((font, index) => (
                <div key={font.key} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {font.key.replace('_', ' ').toUpperCase()}
                  </h3>

                  {/* Font Family Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Android Font Family <span className="text-xs font-normal text-gray-400">(font_family_android)</span>
                      </label>
                      <select
                        value={font.fontFamilyAndroid || font.fontFamily || 'Roboto'}
                        onChange={(e) => {
                          const newTypography = [...typography];
                          newTypography[index].fontFamilyAndroid = e.target.value;
                          setTypography(newTypography);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        style={{ fontFamily: font.fontFamilyAndroid || font.fontFamily }}
                      >
                        <option value="Quicksand" style={{ fontFamily: 'Quicksand' }}>Quicksand (Modern Geometric)</option>
                        <option value="Inter" style={{ fontFamily: 'Inter' }}>Inter (Clean Sans-serif)</option>
                        <option value="Roboto" style={{ fontFamily: 'Roboto' }}>Roboto (Android Default)</option>
                        <option value="Serif" style={{ fontFamily: 'Georgia, serif' }}>Serif / Georgia</option>
                        <option value="Monospace" style={{ fontFamily: 'Courier, monospace' }}>Monospace / Courier</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Or enter custom font..."
                        value={
                          ['Quicksand', 'Inter', 'Roboto', 'Serif', 'Monospace'].includes(font.fontFamilyAndroid || font.fontFamily)
                            ? ''
                            : (font.fontFamilyAndroid || font.fontFamily || '')
                        }
                        onChange={(e) => {
                          const newTypography = [...typography];
                          newTypography[index].fontFamilyAndroid = e.target.value;
                          setTypography(newTypography);
                        }}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        iOS Font Family <span className="text-xs font-normal text-gray-400">(font_family_ios)</span>
                      </label>
                      <select
                        value={font.fontFamilyIos || font.fontFamily || 'SF Pro Rounded'}
                        onChange={(e) => {
                          const newTypography = [...typography];
                          newTypography[index].fontFamilyIos = e.target.value;
                          setTypography(newTypography);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        style={{ fontFamily: font.fontFamilyIos || font.fontFamily }}
                      >
                        <optgroup label="Bundled Fonts">
                          <option value="Quicksand" style={{ fontFamily: 'Quicksand' }}>Quicksand (Modern Geometric)</option>
                          <option value="Inter" style={{ fontFamily: 'Inter' }}>Inter (Clean Sans-serif)</option>
                          <option value="Roboto" style={{ fontFamily: 'Roboto' }}>Roboto (Android Default)</option>
                        </optgroup>
                        <optgroup label="iOS System Fonts">
                          <option value="SF Pro Rounded" style={{ fontFamily: '-apple-system, BlinkMacSystemFont' }}>SF Pro Rounded</option>
                          <option value="SF Pro Text" style={{ fontFamily: '-apple-system, BlinkMacSystemFont' }}>SF Pro Text</option>
                          <option value="SF Pro Display" style={{ fontFamily: '-apple-system, BlinkMacSystemFont' }}>SF Pro Display (iOS Default)</option>
                          <option value="New York" style={{ fontFamily: 'Georgia, serif' }}>New York (Serif)</option>
                          <option value="SF Mono" style={{ fontFamily: 'SFMono-Regular, Menlo, monospace' }}>SF Mono (Monospace)</option>
                        </optgroup>
                        <optgroup label="Pre-installed iOS Fonts">
                          <option value="Avenir" style={{ fontFamily: 'Avenir, sans-serif' }}>Avenir (Modern Geometric)</option>
                          <option value="Avenir Next" style={{ fontFamily: 'Avenir Next, sans-serif' }}>Avenir Next (Clean Sans-serif)</option>
                          <option value="Helvetica Neue" style={{ fontFamily: 'Helvetica Neue, sans-serif' }}>Helvetica Neue (Classic)</option>
                          <option value="Georgia" style={{ fontFamily: 'Georgia, serif' }}>Georgia (Cross-platform Serif)</option>
                          <option value="Menlo" style={{ fontFamily: 'Menlo, monospace' }}>Menlo (Monospace)</option>
                        </optgroup>
                      </select>
                      <p className="text-xs text-gray-500 mt-1 italic">
                        Recommended: SF Pro Rounded for Quicksand, SF Pro Text for Inter, SF Pro Display for Roboto
                      </p>
                      <input
                        type="text"
                        placeholder="Or enter custom font..."
                        value={
                          ['Quicksand', 'Inter', 'Roboto', 'SF Pro Rounded', 'SF Pro Text', 'SF Pro Display', 'New York', 'SF Mono', 'Avenir', 'Avenir Next', 'Helvetica Neue', 'Georgia', 'Menlo'].includes(font.fontFamilyIos || font.fontFamily)
                            ? ''
                            : (font.fontFamilyIos || font.fontFamily || '')
                        }
                        onChange={(e) => {
                          const newTypography = [...typography];
                          newTypography[index].fontFamilyIos = e.target.value;
                          setTypography(newTypography);
                        }}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>

                  {/* Font Properties */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Weight <span className="text-xs font-normal text-gray-400">(font_weight)</span>
                      </label>
                      <select
                        value={font.fontWeight}
                        onChange={(e) => {
                          const newTypography = [...typography];
                          newTypography[index].fontWeight = e.target.value;
                          setTypography(newTypography);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="300">300 - Light</option>
                        <option value="400">400 - Normal</option>
                        <option value="600">600 - Semi Bold</option>
                        <option value="700">700 - Bold</option>
                        <option value="900">900 - Black</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Size (sp) <span className="text-xs font-normal text-gray-400">(font_size_sp)</span>
                      </label>
                      <input
                        type="number"
                        value={font.fontSize}
                        onChange={(e) => {
                          const newTypography = [...typography];
                          newTypography[index].fontSize = e.target.value;
                          setTypography(newTypography);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Line Height <span className="text-xs font-normal text-gray-400">(line_height_multiplier)</span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={font.lineHeight}
                        onChange={(e) => {
                          const newTypography = [...typography];
                          newTypography[index].lineHeight = e.target.value;
                          setTypography(newTypography);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Font Color Reference <span className="text-xs font-normal text-gray-400">(font_color)</span>
                        <span className="text-xs text-gray-500 ml-1">(from Colors tab)</span>
                      </label>
                      <select
                        value={font.fontColor || 'text_primary'}
                        onChange={(e) => {
                          const newTypography = [...typography];
                          newTypography[index].fontColor = e.target.value;
                          setTypography(newTypography);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <optgroup label="Light Theme - Palette">
                          <option value="palette_primary_light">Primary Light</option>
                          <option value="palette_primary_main">Primary Main</option>
                          <option value="palette_primary_dark">Primary Dark</option>
                          <option value="palette_secondary_light">Secondary Light</option>
                          <option value="palette_secondary_main">Secondary Main</option>
                          <option value="palette_secondary_dark">Secondary Dark</option>
                          <option value="palette_accent_light">Accent Light</option>
                          <option value="palette_accent_main">Accent Main</option>
                          <option value="palette_accent_dark">Accent Dark</option>
                        </optgroup>
                        <optgroup label="Light Theme - Text Colors">
                          <option value="text_primary">Text Primary</option>
                          <option value="text_secondary">Text Secondary</option>
                          <option value="text_link">Text Link</option>
                          <option value="text_neutralDark">Text Neutral Dark</option>
                          <option value="text_neutralLight">Text Neutral Light</option>
                        </optgroup>
                        <optgroup label="Light Theme - Status">
                          <option value="status_success">Status Success</option>
                          <option value="status_error">Status Error</option>
                          <option value="status_warning">Status Warning</option>
                          <option value="status_info">Status Info</option>
                        </optgroup>
                        <optgroup label="Dark Theme - Palette">
                          <option value="dark_palette_primary_light">Dark Primary Light</option>
                          <option value="dark_palette_primary_main">Dark Primary Main</option>
                          <option value="dark_palette_primary_dark">Dark Primary Dark</option>
                          <option value="dark_palette_secondary_light">Dark Secondary Light</option>
                          <option value="dark_palette_secondary_main">Dark Secondary Main</option>
                          <option value="dark_palette_secondary_dark">Dark Secondary Dark</option>
                          <option value="dark_palette_accent_light">Dark Accent Light</option>
                          <option value="dark_palette_accent_main">Dark Accent Main</option>
                          <option value="dark_palette_accent_dark">Dark Accent Dark</option>
                        </optgroup>
                        <optgroup label="Dark Theme - Text Colors">
                          <option value="dark_text_primary">Dark Text Primary</option>
                          <option value="dark_text_secondary">Dark Text Secondary</option>
                          <option value="dark_text_link">Dark Text Link</option>
                          <option value="dark_text_neutralDark">Dark Text Neutral Dark</option>
                          <option value="dark_text_neutralLight">Dark Text Neutral Light</option>
                        </optgroup>
                        <optgroup label="Dark Theme - Status">
                          <option value="dark_status_success">Dark Status Success</option>
                          <option value="dark_status_error">Dark Status Error</option>
                          <option value="dark_status_warning">Dark Status Warning</option>
                          <option value="dark_status_info">Dark Status Info</option>
                        </optgroup>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Features Tab */}
        {activeTab === 'features' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Feature Flags</h2>
            <div className="space-y-3">
              {features.map((feature, index) => (
                <div key={feature.key} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {feature.key === 'hidden_gem' ? '💎 Hidden Gems' : feature.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </h3>
                      {feature.key === 'hidden_gem' && (
                        <p className="text-sm text-gray-500 mt-0.5">Highlights rarely-completed challenges compared to popular ones</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleFeatureToggle(index)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        feature.enabled ? 'bg-primary-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          feature.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  {/* Hidden Gem config fields */}
                  {feature.key === 'hidden_gem' && feature.enabled && (
                    <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Time Window (weeks)</label>
                        <input
                          type="number"
                          min="1"
                          max="52"
                          value={feature.config?.weeks || 8}
                          onChange={(e) => handleFeatureConfigChange(index, 'weeks', parseInt(e.target.value) || 8)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <p className="text-xs text-gray-400 mt-1">Compare completions over this period</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ratio Threshold</label>
                        <input
                          type="number"
                          min="0.05"
                          max="1"
                          step="0.05"
                          value={feature.config?.ratio || 0.3}
                          onChange={(e) => handleFeatureConfigChange(index, 'ratio', parseFloat(e.target.value) || 0.3)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <p className="text-xs text-gray-400 mt-1">Below this % of avg = hidden gem</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Min Popular Completions</label>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={feature.config?.minPopular || 2}
                          onChange={(e) => handleFeatureConfigChange(index, 'minPopular', parseInt(e.target.value) || 2)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <p className="text-xs text-gray-400 mt-1">Minimum activity before gems activate</p>
                      </div>
                    </div>
                  )}
                  {/* Generic config display for other features */}
                  {feature.key !== 'hidden_gem' && feature.config && (
                    <p className="text-sm text-gray-500 mt-2">
                      Config: {JSON.stringify(feature.config)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Challenge Categories</h2>
                <p className="text-sm text-gray-500 mt-1">Manage challenge categories for this deployment. Enable/disable standard categories or add custom ones.</p>
              </div>
              <button
                onClick={fetchCategories}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                <FiRefreshCw className={loadingCategories ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>

            {/* Standard Categories */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700">Standard Categories</h3>
                <p className="text-xs text-gray-500 mt-0.5">Toggle categories on/off for this deployment. The General category is always enabled.</p>
              </div>
              <div className="divide-y divide-gray-100">
                {categories.filter(c => c.isStandard).map(category => {
                  const count = categoryCounts[category.id] || 0;
                  return (
                    <div key={category.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: category.color + '20' }}
                        >
                          <LucideIcon name={category.icon} size={20} color={category.color} />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{category.name}</div>
                          <div className="text-xs text-gray-500">{category.slug}</div>
                        </div>
                        {category.isDefault && (
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Default</span>
                        )}
                        <span className={`px-2 py-0.5 text-xs rounded ${count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {count} challenge{count !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Marker Icon */}
                        <div className="flex items-center gap-2">
                          {category.markerIconUrl ? (
                            <div className="relative group">
                              <img
                                src={category.markerIconUrl}
                                alt="Marker"
                                className="w-8 h-8 p-1 rounded border border-gray-200"
                                style={{ backgroundColor: category.color }}
                              />
                              <button
                                onClick={() => handleRemoveMarkerIcon(category.id)}
                                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs hidden group-hover:flex items-center justify-center"
                                title="Remove marker icon"
                              >
                                <FiX size={10} />
                              </button>
                            </div>
                          ) : (
                            <label className="cursor-pointer p-2 border border-dashed border-gray-300 rounded hover:border-blue-400 hover:bg-blue-50 transition-colors" title="Upload marker icon (24x24 or 48x48 white PNG)">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleMarkerIconUpload(category.id, e.target.files[0])}
                              />
                              {uploadingMarkerIcon === category.id ? (
                                <FiRefreshCw size={16} className="animate-spin text-blue-500" />
                              ) : (
                                <FiUpload size={16} className="text-gray-400" />
                              )}
                            </label>
                          )}
                        </div>
                        <label className={`relative inline-flex items-center ${category.isDefault || count > 0 ? 'cursor-not-allowed' : 'cursor-pointer'}`} title={count > 0 && category.isEnabled ? `Cannot disable: ${count} challenge${count !== 1 ? 's' : ''} assigned` : ''}>
                          <input
                            type="checkbox"
                            checked={category.isEnabled}
                            onChange={() => handleToggleCategory(category.id, category.isEnabled)}
                            disabled={category.isDefault}
                            className="sr-only peer"
                          />
                          <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${category.isDefault ? 'opacity-50' : ''}`}></div>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Custom Categories */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                <h3 className="text-sm font-semibold text-gray-700">Custom Categories</h3>
                <p className="text-xs text-gray-500 mt-0.5">Add region-specific categories like "Kayaking", "Wine Tasting", etc.</p>
              </div>

              {/* Existing Custom Categories */}
              <div className="divide-y divide-gray-100">
                {categories.filter(c => !c.isStandard).map(category => {
                  const count = categoryCounts[category.id] || 0;
                  return (
                    <div key={category.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <IconPicker
                          selectedIcon={category.icon}
                          color={category.color}
                          onSelect={(icon) => handleUpdateCategory(category.id, { icon })}
                          size="md"
                        />
                        <div className="flex-1">
                          <input
                            type="text"
                            value={category.name}
                            onChange={(e) => {
                              const newName = e.target.value;
                              setCategories(prev => prev.map(c =>
                                c.id === category.id ? { ...c, name: newName } : c
                              ));
                            }}
                            onBlur={(e) => handleUpdateCategory(category.id, { name: e.target.value })}
                            className="w-full px-2 py-1 text-sm border border-transparent hover:border-gray-300 focus:border-blue-500 rounded focus:outline-none"
                          />
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded whitespace-nowrap ${count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {count} challenge{count !== 1 ? 's' : ''}
                        </span>
                        <input
                          type="color"
                          value={category.color}
                          onChange={(e) => handleUpdateCategory(category.id, { color: e.target.value })}
                          className="w-8 h-8 rounded cursor-pointer border border-gray-300"
                        />
                        {/* Marker Icon */}
                        {category.markerIconUrl ? (
                          <div className="relative group">
                            <img
                              src={category.markerIconUrl}
                              alt="Marker"
                              className="w-8 h-8 p-1 rounded border border-gray-200"
                              style={{ backgroundColor: category.color }}
                            />
                            <button
                              onClick={() => handleRemoveMarkerIcon(category.id)}
                              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs hidden group-hover:flex items-center justify-center"
                              title="Remove marker icon"
                            >
                              <FiX size={10} />
                            </button>
                          </div>
                        ) : (
                          <label className="cursor-pointer p-2 border border-dashed border-gray-300 rounded hover:border-blue-400 hover:bg-blue-50 transition-colors" title="Upload marker icon (24x24 or 48x48 white PNG)">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleMarkerIconUpload(category.id, e.target.files[0])}
                            />
                            {uploadingMarkerIcon === category.id ? (
                              <FiRefreshCw size={16} className="animate-spin text-blue-500" />
                            ) : (
                              <FiUpload size={16} className="text-gray-400" />
                            )}
                          </label>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <label className={`relative inline-flex items-center ${count > 0 ? 'cursor-not-allowed' : 'cursor-pointer'}`} title={count > 0 && category.isEnabled ? `Cannot disable: ${count} challenge${count !== 1 ? 's' : ''} assigned` : ''}>
                          <input
                            type="checkbox"
                            checked={category.isEnabled}
                            onChange={() => handleToggleCategory(category.id, category.isEnabled)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete category"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {categories.filter(c => !c.isStandard).length === 0 && (
                  <div className="px-4 py-6 text-center text-gray-500 text-sm">
                    No custom categories yet. Add one below.
                  </div>
                )}
              </div>

              {/* Add New Category Form */}
              <div className="px-4 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center gap-3">
                  <IconPicker
                    selectedIcon={newCategory.icon}
                    color={newCategory.color}
                    onSelect={(icon) => setNewCategory(prev => ({ ...prev, icon }))}
                    size="md"
                  />
                  <input
                    type="text"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="New category name (e.g., Kayaking)"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                  <input
                    type="color"
                    value={newCategory.color}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
                    className="w-10 h-10 rounded-lg cursor-pointer border border-gray-300"
                  />
                  <button
                    onClick={handleCreateCategory}
                    disabled={savingCategory || !newCategory.name.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiPlus size={16} />
                    {savingCategory ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <div className="text-blue-600 mt-0.5">
                  <FiMapPin size={20} />
                </div>
                <div>
                  <h4 className="font-medium text-blue-800">How categories work</h4>
                  <ul className="mt-2 text-sm text-blue-700 space-y-1">
                    <li>Each challenge is assigned to one category</li>
                    <li>Categories appear as filter options in the mobile app</li>
                    <li>Disabled categories hide challenges from that category in filters</li>
                    <li>The "General" category is always enabled and is the default for new challenges</li>
                    <li>Deleting a custom category moves its challenges to "General"</li>
                  </ul>
                  <h4 className="font-medium text-blue-800 mt-4">Map Marker Icons</h4>
                  <ul className="mt-2 text-sm text-blue-700 space-y-1">
                    <li>Upload a custom marker icon (PNG, SVG, or WebP) for each category</li>
                    <li>Use a white silhouette on transparent background (24x24 or 48x48 pixels)</li>
                    <li>The mobile app draws a colored pin background and overlays the icon</li>
                    <li>If no marker icon is set, the app uses a plain colored pin</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Share Settings Tab */}
        {activeTab === 'share' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Challenge Sharing Settings</h2>
                <p className="text-sm text-gray-500 mt-1">Configure how users can share challenges from the mobile app</p>
              </div>
              <button
                onClick={async () => {
                  try {
                    setSavingShareSettings(true);
                    const token = localStorage.getItem('cms_accessToken');
                    await axios.put(
                      `${API_URL}/v1/app-config/discover-flaeming/share`,
                      shareSettings,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    alert('Share settings saved successfully!');
                  } catch (err) {
                    console.error('Error saving share settings:', err);
                    alert('Failed to save share settings');
                  } finally {
                    setSavingShareSettings(false);
                  }
                }}
                disabled={savingShareSettings}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                <FiSave className="w-4 h-4" />
                {savingShareSettings ? t('cms.common.loading') : t('cms.common.save')}
              </button>
            </div>

            {/* Global Enable Toggle */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Enable Challenge Sharing</h3>
                  <p className="text-sm text-gray-500">Allow users to share challenges with others</p>
                </div>
                <button
                  onClick={() => setShareSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${shareSettings.enabled ? 'bg-primary-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${shareSettings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            {shareSettings.enabled && (
              <>
                {/* Link Format */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <FiLink className="w-4 h-4" />
                    Link Format
                  </h3>
                  <input
                    type="text"
                    value={shareSettings.linkFormat}
                    onChange={(e) => setShareSettings(prev => ({ ...prev, linkFormat: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                    placeholder="https://exploreandwin.com/c/{id}"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Use <code className="bg-gray-100 px-1 rounded">{'{id}'}</code> for challenge ID, <code className="bg-gray-100 px-1 rounded">{'{slug}'}</code> for URL-friendly name
                  </p>
                </div>

                {/* Share Text Template */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <FiMessageCircle className="w-4 h-4" />
                    Share Text Template
                  </h3>
                  <textarea
                    value={shareSettings.textTemplate}
                    onChange={(e) => setShareSettings(prev => ({ ...prev, textTemplate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    rows={2}
                    placeholder="Check out this challenge: {name} - {link}"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Variables: <code className="bg-gray-100 px-1 rounded">{'{name}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{link}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{points}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{difficulty}'}</code>
                  </p>
                </div>

                {/* Share Platforms */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <FiShare2 className="w-4 h-4" />
                    Share Platforms
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(shareSettings.platforms || {}).map(([key, platform]) => (
                      <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">
                            {key === 'whatsapp' && '💬'}
                            {key === 'x' && '𝕏'}
                            {key === 'facebook' && '📘'}
                            {key === 'telegram' && '✈️'}
                            {key === 'instagram' && '📷'}
                            {key === 'copyLink' && '🔗'}
                            {key === 'nativeShare' && '📤'}
                          </span>
                          <div>
                            <span className="font-medium text-gray-900">{platform.label}</span>
                            {platform.note && (
                              <span className="ml-2 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">{platform.note}</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => setShareSettings(prev => ({
                            ...prev,
                            platforms: {
                              ...prev.platforms,
                              [key]: { ...platform, enabled: !platform.enabled }
                            }
                          }))}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${platform.enabled ? 'bg-primary-600' : 'bg-gray-300'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${platform.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h3 className="font-medium text-blue-900 mb-2">Preview</h3>
                  <p className="text-sm text-blue-800">
                    <strong>Link:</strong> {shareSettings.linkFormat.replace('{id}', 'abc123').replace('{slug}', 'marienplatz-discovery')}
                  </p>
                  <p className="text-sm text-blue-800 mt-1">
                    <strong>Text:</strong> {shareSettings.textTemplate
                      .replace('{name}', 'Marienplatz Glockenspiel Discovery')
                      .replace('{link}', shareSettings.linkFormat.replace('{id}', 'abc123'))
                      .replace('{points}', '15')
                      .replace('{difficulty}', 'Easy')}
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    <strong>Enabled platforms:</strong> {Object.entries(shareSettings.platforms || {})
                      .filter(([_, p]) => p.enabled)
                      .map(([_, p]) => p.label)
                      .join(', ') || 'None'}
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Strings Tab */}
        {activeTab === 'strings' && (
          <StringsTab
            availableLanguages={availableLanguages}
            currentLanguage={currentLanguage}
            setCurrentLanguage={setCurrentLanguage}
            deploymentKey="discover-flaeming"
          />
        )}

      </div>

      {/* Crop Modal for Onboarding Images */}
      {cropModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Crop Onboarding Image</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Screen {cropModal.screenIndex + 1} - {cropModal.mode === 'light' ? 'Light Mode' : 'Dark Mode'}
                </p>
              </div>
              <button
                onClick={() => setCropModal({ isOpen: false, imageUrl: '', screenIndex: null, mode: null })}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Preset Buttons */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <p className="text-sm font-medium text-gray-700 mb-2">Quick Presets:</p>
              <div className="flex flex-wrap gap-2">
                {cropPresets.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => setCrop(preset.crop)}
                    className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-primary-50 hover:border-primary-500 hover:text-primary-700 transition-colors"
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Crop Area */}
            <div className="flex-1 overflow-auto p-6 bg-gray-900">
              <div className="flex items-center justify-center min-h-[60vh]">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                >
                  <img
                    ref={cropImgRef}
                    src={cropModal.imageUrl}
                    alt="Crop preview"
                    style={{ maxHeight: '70vh', maxWidth: '100%', height: 'auto', width: 'auto' }}
                    crossOrigin="anonymous"
                  />
                </ReactCrop>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setCropModal({ isOpen: false, imageUrl: '', screenIndex: null, mode: null })}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                {t('cms.common.cancel')}
              </button>
              <button
                onClick={handleCropSave}
                disabled={croppingSaving}
                className="btn btn-primary transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {croppingSaving ? (
                  <>
                    <FiRefreshCw className="w-4 h-4 animate-spin" />
                    Cropping...
                  </>
                ) : (
                  <>
                    <FiCrop className="w-4 h-4" />
                    Apply Crop
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Background Image Gallery Picker for Teasers */}
      <GalleryImagePicker
        isOpen={showBgImagePicker.open}
        onClose={() => setShowBgImagePicker({ open: false, teaserIndex: null })}
        onSelect={(imageUrl) => {
          if (showBgImagePicker.teaserIndex !== null) {
            const newTeasers = [...teasers];
            newTeasers[showBgImagePicker.teaserIndex] = {
              ...newTeasers[showBgImagePicker.teaserIndex],
              backgroundImageUrl: imageUrl
            };
            setTeasers(newTeasers);
          }
        }}
        selectedImageUrl={showBgImagePicker.teaserIndex !== null ? teasers[showBgImagePicker.teaserIndex]?.backgroundImageUrl : ''}
        defaultSource="challenge-gallery"
      />

      {/* Main Image Gallery Picker for Teasers */}
      <GalleryImagePicker
        isOpen={showMainImagePicker.open}
        onClose={() => setShowMainImagePicker({ open: false, teaserIndex: null })}
        onSelect={(imageUrl) => {
          if (showMainImagePicker.teaserIndex !== null) {
            const newTeasers = [...teasers];
            newTeasers[showMainImagePicker.teaserIndex] = {
              ...newTeasers[showMainImagePicker.teaserIndex],
              imageUrl: imageUrl
            };
            setTeasers(newTeasers);
          }
        }}
        selectedImageUrl={showMainImagePicker.teaserIndex !== null ? teasers[showMainImagePicker.teaserIndex]?.imageUrl : ''}
        defaultSource="challenge-gallery"
      />

      {/* Image Preview Modal */}
      {imagePreview.isOpen && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setImagePreview({ isOpen: false, url: '', title: '' })}
        >
          <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center">
            {/* Header */}
            <div className="absolute -top-10 left-0 right-0 flex justify-between items-center text-white">
              <span className="text-lg font-medium">{imagePreview.title}</span>
              <button
                onClick={() => setImagePreview({ isOpen: false, url: '', title: '' })}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            {/* Image */}
            <img
              src={imagePreview.url}
              alt={imagePreview.title}
              className="max-h-[85vh] max-w-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            {/* Footer hint */}
            <p className="text-gray-400 text-sm mt-4">Click outside or press X to close</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppSettings;
